import React, { useState } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, PieChart, Pie, AreaChart, Area, ScatterChart, Scatter
} from 'recharts';
import styles from './DetailedAnalysis.module.css';

const DetailedAnalysis = ({ 
  recommendationData, 
  evaluationData, 
  consolidatedData,
  userPlan = 'essential',
  hasActiveSubscription = false
}) => {
  const [activeSection, setActiveSection] = useState('comprehensive-scoring');
  
  // Add state for contractor visibility toggle
  const [activeContractors, setActiveContractors] = useState(null);
  
  // Add state for expanded score explanations
  const [expandedScoreExplanations, setExpandedScoreExplanations] = useState({});
  
  // Toggle function for score explanations
  const toggleScoreExplanation = (categoryIndex) => {
    setExpandedScoreExplanations(prev => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex]
    }));
  };
  
  // Function to scroll to category definition
  const scrollToCategoryDefinition = (categoryShortName) => {
    const categoryId = `category-${categoryShortName.toLowerCase()}`;
    const element = document.getElementById(categoryId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
      
      // Add a highlight effect
      element.classList.add(styles.highlighted);
      setTimeout(() => {
        element.classList.remove(styles.highlighted);
      }, 2000);
    }
  };
  
  // Get evaluation explanation for a specific category and contractor
  const getEvaluationExplanation = (categoryShortName, contractorIndex) => {
    if (!evaluationData) return null;
    
    const evalKey = `contractor_${contractorIndex + 1}`;
    const contractorEval = evaluationData[evalKey];
    if (!contractorEval) return null;
    
    // Map category short names to evaluation data keys
    const categoryMap = {
      'Communication': 'communication_and_accountability',
      'Documentation': 'documentation_and_deliverables',
      'Logistics': 'logistics_and_execution_plan',
      'Innovation': 'innovation_and_value_adds',
      'Timeline': 'timeline_and_schedule_factors',
      'Scope': 'scope_and_methodology',
      'Safety': 'safety_measurements',
      'Labor': 'labor_and_workforce',
      'Contracting': 'contracting_behavior',
      'Recordkeeping': 'documentation_and_recordkeeping',
      'Execution': 'logistics_and_execution_plan'
    };
    
    const categoryKey = categoryMap[categoryShortName];
    if (!categoryKey || !contractorEval[categoryKey]) return null;
    
    const categoryData = contractorEval[categoryKey];
    return {
      score: categoryData.score || 0,
      verdict: categoryData.verdict || 'Unknown',
      rationale: categoryData.rationale || 'No detailed explanation available.',
      contractorName: contractorEval.contractor_name
    };
  };
  
  // Utility function to safely convert scores to numbers
  const safeParseScore = (score) => {
    if (score === undefined || score === null) return 0;
    if (typeof score === 'string') return parseFloat(score) || 0;
    if (typeof score === 'number') return score;
    return 0;
  };
  
  // Plan-based feature access control
  const canAccessBasicDetailedAnalysis = () => {
    // Essential+ plans get basic detailed analysis
    return hasActiveSubscription && ['essential', 'complete', 'expert'].includes(userPlan);
  };

  const canAccessComprehensiveAnalysis = () => {
    // Complete+ plans get comprehensive analysis with all features
    return hasActiveSubscription && ['complete', 'expert'].includes(userPlan);
  };

  const canAccessAdvancedFeatures = () => {
    // Expert plan gets advanced features like risk modeling, negotiation guides
    return hasActiveSubscription && userPlan === 'expert';
  };

  const canAccessFinancialDeepDive = () => {
    // Complete+ plans get financial deep dive analysis
    return hasActiveSubscription && ['complete', 'expert'].includes(userPlan);
  };

  const canAccessRiskAssessment = () => {
    // Essential+ gets basic risk assessment, Complete+ gets comprehensive risk assessment
    return hasActiveSubscription && ['essential', 'complete', 'expert'].includes(userPlan);
  };

  const canAccessContractAnalysis = () => {
    // Complete+ plans get contract analysis
    return hasActiveSubscription && ['complete', 'expert'].includes(userPlan);
  };

  const canAccessTechnicalSpecs = () => {
    // Complete+ plans get technical specifications analysis
    return hasActiveSubscription && ['complete', 'expert'].includes(userPlan);
  };

  // Extract contractor data (supports up to 5 contractors maximum)
  const contractors = consolidatedData ? 
    [1, 2, 3, 4, 5]
      .map(num => consolidatedData[`contractor_${num}`])
      .filter(Boolean) 
    : [];

  // Color scheme for contractors
  const getContractorColors = () => {
    const colors = ['#0077cc', '#ff9800', '#4caf50', '#9c27b0', '#f44336'];
    const contractorColors = {};
    contractors.forEach((contractor, index) => {
      const name = contractor.contractor.contractor_name;
      contractorColors[name] = colors[index % colors.length];
    });
    return contractorColors;
  };

  const contractorColors = getContractorColors();

  // Initialize activeContractors when contractors are loaded
  React.useEffect(() => {
    if (contractors.length > 0 && !activeContractors) {
      const initialActiveContractors = {};
      contractors.forEach(contractor => {
        initialActiveContractors[contractor.contractor.contractor_name] = true;
      });
      setActiveContractors(initialActiveContractors);
    }
  }, [contractors, activeContractors]);



  // Toggle contractor visibility
  const toggleContractor = (contractorName) => {
    setActiveContractors(prev => ({
      ...prev,
      [contractorName]: !prev[contractorName]
    }));
  };

  // Comprehensive scoring analysis
  const prepareComprehensiveScoring = () => {
    if (!evaluationData) return [];
    
    // First, try with predefined categories
    const predefinedCategories = [
      'communication_and_accountability',
      'documentation_and_deliverables',
      'logistics_and_execution_plan',
      'innovation_and_value_adds',
      'timeline_and_schedule_factors',
      'scope_and_methodology',
      'safety_measurements',
      'labor_and_workforce',
      'documentation_and_recordkeeping',
      'contracting_behavior'
    ];

    let radarData = predefinedCategories.map(category => {
      const dataPoint = { 
        category: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        shortName: category.split('_')[0].charAt(0).toUpperCase() + category.split('_')[0].slice(1)
      };
      
      contractors.forEach((contractor, index) => {
        const evalKey = `contractor_${index + 1}`;
        const categoryData = evaluationData[evalKey]?.[category];
        
        // Get score with fallback logic
        let score = 0;
        if (categoryData?.score !== undefined) {
          score = safeParseScore(categoryData.score);
        } else if (categoryData?.verdict) {
          // Fallback: convert verdict to score
          switch (categoryData.verdict.toLowerCase()) {
            case 'strong': score = 9; break;
            case 'adequate': score = 6; break;
            case 'unclear': score = 3; break;
            case 'missing': score = 0; break;
            default: score = 5; break;
          }
        }
        
        dataPoint[contractor.contractor.contractor_name] = score;
      });
      
      return dataPoint;
    }).filter(dataPoint => {
      // Only include categories that have at least one non-zero score
      const hasData = contractors.some(contractor => 
        dataPoint[contractor.contractor.contractor_name] > 0
      );
      return hasData;
    });

    // If we don't have enough data, create a fallback with available categories
    if (radarData.length < 3 && evaluationData.contractor_1) {
      const availableCategories = Object.keys(evaluationData.contractor_1).filter(key => 
        typeof evaluationData.contractor_1[key] === 'object' && 
        evaluationData.contractor_1[key] !== null &&
        (evaluationData.contractor_1[key].score !== undefined || evaluationData.contractor_1[key].verdict)
      );

      radarData = availableCategories.slice(0, 8).map(category => {
        const dataPoint = { 
          category: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          shortName: category.split('_')[0].charAt(0).toUpperCase() + category.split('_')[0].slice(1)
        };
        
        contractors.forEach((contractor, index) => {
          const evalKey = `contractor_${index + 1}`;
          const categoryData = evaluationData[evalKey]?.[category];
          
          let score = 0;
          if (categoryData?.score !== undefined) {
            score = safeParseScore(categoryData.score);
          } else if (categoryData?.verdict) {
            switch (categoryData.verdict.toLowerCase()) {
              case 'strong': score = 9; break;
              case 'adequate': score = 6; break;
              case 'unclear': score = 3; break;
              case 'missing': score = 0; break;
              default: score = 5; break;
            }
          }
          
          dataPoint[contractor.contractor.contractor_name] = score;
        });
        
        return dataPoint;
      });
    }

    return radarData.slice(0, 8); // Limit to max 8 categories for better chart readability
  };

  // Financial analysis data
  const prepareFinancialAnalysis = () => {
    return contractors.map(contractor => {
      const totalCost = parseFloat(contractor.payment_terms.total_cost.replace(/[\$,]/g, '')) || 0;
      const allowancesTotal = Object.values(contractor.allowances?.direct_costs || {})
        .reduce((sum, val) => {
          const numVal = typeof val === 'string' ? parseFloat(val.replace(/[\$,]/g, '')) : val;
          return sum + (numVal || 0);
        }, 0);
      
      return {
        name: contractor.contractor.contractor_name,
        shortName: contractor.contractor.contractor_name.split(' ')[0],
        totalCost,
        allowancesTotal,
        basePrice: totalCost - allowancesTotal,
        costPerSqFt: Math.round(totalCost / 1200), // Assuming ~1200 sq ft project
        priceRank: 0 // Will be calculated later
      };
    }).sort((a, b) => a.totalCost - b.totalCost)
      .map((item, index) => ({ ...item, priceRank: index + 1 }));
  };

  // Risk assessment matrix
  const prepareRiskAssessment = () => {
    return contractors.map((contractor, index) => {
      const evalKey = `contractor_${index + 1}`;
      const contractorEval = evaluationData?.[evalKey];
      const safetyScore = safeParseScore(contractorEval?.safety_measurements?.score);
      const riskItems = contractorEval?.risk || [];
      
      return {
        name: contractor.contractor.contractor_name,
        shortName: contractor.contractor.contractor_name.split(' ')[0],
        safetyScore,
        riskCount: riskItems.length,
        riskLevel: riskItems.length > 2 ? 'High' : riskItems.length > 0 ? 'Medium' : 'Low',
        missingInfo: contractorEval?.missing_information?.length || 0
      };
    });
  };

  // Detailed payment analysis
  const preparePaymentAnalysis = () => {
    return contractors.map(contractor => {
      const totalCost = parseFloat(contractor.payment_terms.total_cost.replace(/[\$,]/g, '')) || 0;
      const deposit = parseFloat(contractor.payment_terms.deposit_required?.replace(/[\$,]/g, '')) || 0;
      
      return {
        name: contractor.contractor.contractor_name,
        shortName: contractor.contractor.contractor_name.split(' ')[0],
        totalCost,
        deposit,
        depositPercent: Math.round((deposit / totalCost) * 100),
        paymentStructure: contractor.payment_terms.pricing_structure,
        warrantyYears: contractor.warranty_details?.workmanship?.warranty_duration?.includes('year') ? 
          parseInt(contractor.warranty_details.workmanship.warranty_duration) : 0
      };
    });
  };

  const comprehensiveScoring = prepareComprehensiveScoring();
  const financialAnalysis = prepareFinancialAnalysis();
  const riskAssessment = prepareRiskAssessment();
  const paymentAnalysis = preparePaymentAnalysis();

  // Debug logging to check data
  console.log('DetailedAnalysis - Data check:', {
    contractors: contractors.length,
    contractorNames: contractors.map(c => c.contractor.contractor_name),
    evaluationData: !!evaluationData,
    evaluationDataKeys: evaluationData ? Object.keys(evaluationData) : [],
    contractor1Categories: evaluationData?.contractor_1 ? Object.keys(evaluationData.contractor_1) : [],
    comprehensiveScoring: comprehensiveScoring.slice(0, 2), // First 2 items for debugging
    comprehensiveScoringLength: comprehensiveScoring.length,
    contractorColors
  });

  // Locked content component
  const LockedContent = ({ sectionTitle, sectionDescription, requiredPlan }) => (
    <div className={styles.lockedContentContainer}>
      <div className={styles.lockedContent}>
        <div className={styles.lockedIcon}>
          <i className="fas fa-lock"></i>
        </div>
        <h3>{sectionTitle}</h3>
        <p className={styles.lockedDescription}>
          {sectionDescription}
        </p>
        <div className={styles.upgradePrompt}>
          <h4>🚀 Unlock Premium Analysis</h4>
          <p>
            Upgrade to the <strong>{requiredPlan}</strong> plan to access this detailed analysis and make more informed decisions.
          </p>
          <div className={styles.premiumFeatures}>
            <div className={styles.featureItem}>
              <i className="fas fa-check-circle"></i>
              <span>Comprehensive scoring</span>
            </div>
            <div className={styles.featureItem}>
              <i className="fas fa-check-circle"></i>
              <span>Advanced risk assessment</span>
            </div>
            <div className={styles.featureItem}>
              <i className="fas fa-check-circle"></i>
              <span>Contractor analysis</span>
            </div>
            <div className={styles.featureItem}>
              <i className="fas fa-check-circle"></i>
              <span>Financial deep dive</span>
            </div>
          </div>
          <div className={styles.upgradeButtons}>
            <button className={styles.upgradeButton}>
              <i className="fas fa-arrow-up"></i>
              Upgrade to {requiredPlan}
            </button>
            <button className={styles.learnMoreButton}>
              Learn More About Plans
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Section navigation - show all sections but mark locked ones
  const sections = [
    { id: 'comprehensive-scoring', label: 'Comprehensive Scoring', icon: 'fa-chart-radar', requiredAccess: 'basic' },
    { id: 'financial-deep-dive', label: 'Financial Deep Dive', icon: 'fa-calculator', requiredAccess: 'comprehensive' },
    { id: 'risk-assessment', label: 'Risk Assessment', icon: 'fa-shield-alt', requiredAccess: 'basic' },
    { id: 'contract-analysis', label: 'Contract Analysis', icon: 'fa-file-contract', requiredAccess: 'comprehensive' },
    { id: 'technical-specs', label: 'Technical Specifications', icon: 'fa-cogs', requiredAccess: 'comprehensive' }
  ];

  // Check if a section is accessible
  const isSectionAccessible = (section) => {
    if (section.requiredAccess === 'basic') {
      return canAccessBasicDetailedAnalysis();
    } else if (section.requiredAccess === 'comprehensive') {
      return canAccessComprehensiveAnalysis();
    } else if (section.requiredAccess === 'advanced') {
      return canAccessAdvancedFeatures();
    }
    return true;
  };
  
  // Enhanced Risk assessment analysis with intelligent scoring
  const prepareAdvancedRiskAssessment = () => {
    return contractors.map((contractor, index) => {
      const evalKey = `contractor_${index + 1}`;
      const contractorEval = evaluationData?.[evalKey];
      const riskItems = contractorEval?.risk || [];
      const missing = contractorEval?.missing_information || [];
      
      // Advanced risk severity analysis
      const analyzeRiskSeverity = (riskText) => {
        const riskLower = riskText.toLowerCase();
        
        // Critical risks (major impact)
        const criticalKeywords = ['liability', 'non-compliance', 'legal action', 'unsafe', 'violation', 'lawsuit', 'injury', 'collapse', 'structural'];
        // High risks (significant impact)
        const highKeywords = ['delay', 'cost overrun', 'permit', 'code', 'inspection', 'budget', 'timeline', 'schedule'];
        // Medium risks (moderate impact)
        const mediumKeywords = ['additional costs', 'unclear terms', 'approval', 'coordination', 'communication'];
        // Low risks (minor impact)
        const lowKeywords = ['minor', 'cosmetic', 'preference', 'optional'];
        
        if (criticalKeywords.some(keyword => riskLower.includes(keyword))) {
          return { severity: 'critical', impact: 9, description: 'Critical Risk' };
        }
        if (highKeywords.some(keyword => riskLower.includes(keyword))) {
          return { severity: 'high', impact: 6, description: 'High Risk' };
        }
        if (mediumKeywords.some(keyword => riskLower.includes(keyword))) {
          return { severity: 'medium', impact: 4, description: 'Medium Risk' };
        }
        if (lowKeywords.some(keyword => riskLower.includes(keyword))) {
          return { severity: 'low', impact: 2, description: 'Low Risk' };
        }
        
        // Default to medium for unclassified risks
        return { severity: 'medium', impact: 4, description: 'Medium Risk' };
      };

      // Intelligent risk categorization with better logic
      const categorizeRisks = (risks) => {
        const categories = {
          financial: [],
          safety: [],
          execution: [],
          legal: [],
          communication: []
        };
        
        risks.forEach(risk => {
          const riskLower = risk.toLowerCase();
          const severity = analyzeRiskSeverity(risk);
          const riskWithSeverity = { text: risk, ...severity };
          
          // Financial risks
          if (riskLower.includes('cost') || riskLower.includes('price') || riskLower.includes('budget') || 
              riskLower.includes('expense') || riskLower.includes('payment') || riskLower.includes('overrun')) {
            categories.financial.push(riskWithSeverity);
          }
          // Safety risks (highest priority)
          else if (riskLower.includes('safety') || riskLower.includes('osha') || riskLower.includes('accident') || 
                   riskLower.includes('injury') || riskLower.includes('liability') || riskLower.includes('unsafe')) {
            categories.safety.push(riskWithSeverity);
          }
          // Legal/compliance risks
          else if (riskLower.includes('legal') || riskLower.includes('compliance') || riskLower.includes('code') || 
                   riskLower.includes('regulation') || riskLower.includes('permit') || riskLower.includes('license')) {
            categories.legal.push(riskWithSeverity);
          }
          // Execution/timeline risks
          else if (riskLower.includes('delay') || riskLower.includes('timeline') || riskLower.includes('schedule') || 
                   riskLower.includes('execution') || riskLower.includes('completion')) {
            categories.execution.push(riskWithSeverity);
          }
          // Communication risks
          else if (riskLower.includes('communication') || riskLower.includes('documentation') || 
                   riskLower.includes('approval') || riskLower.includes('planning') || riskLower.includes('coordination')) {
            categories.communication.push(riskWithSeverity);
          }
          // Default to execution if uncategorized
          else {
            categories.execution.push(riskWithSeverity);
          }
        });
        
        return categories;
      };

      const riskCategories = categorizeRisks(riskItems);
      
      // Multi-factor scoring system
      const calculateCategoryScore = (categoryRisks, additionalFactors = {}) => {
        let baseScore = 10;
        
        // Deduct points based on risk severity
        categoryRisks.forEach(risk => {
          baseScore -= (risk.impact / 10) * 2; // Convert impact to point deduction
        });
        
        // Apply additional factors
        Object.entries(additionalFactors).forEach(([factor, adjustment]) => {
          baseScore += adjustment;
        });
        
        return Math.max(0, Math.min(10, baseScore));
      };

      // Enhanced scoring with additional factors
      const totalRisks = riskItems.length;
      
      // Financial score with payment structure consideration
      const depositAmount = parseFloat(contractor.payment_terms?.deposit_required?.replace(/[\$,]/g, '')) || 0;
      const totalCost = parseFloat(contractor.payment_terms?.total_cost?.replace(/[\$,]/g, '')) || 1;
      const depositPercentage = (depositAmount / totalCost) * 100;
      const paymentAdjustment = depositPercentage > 50 ? -1.5 : depositPercentage < 10 ? +0.5 : 0;
      
      const financialScore = calculateCategoryScore(riskCategories.financial, {
        payment_structure: paymentAdjustment,
        change_order_policy: contractor.change_order_policy?.explanation ? +0.5 : -0.5
      });
      
      // Safety score from evaluation data with risk adjustments
      const baseSafetyScore = safeParseScore(contractorEval?.safety_measurements?.score);
      const safetyRiskPenalty = riskCategories.safety.reduce((penalty, risk) => penalty + (risk.impact / 10), 0);
      const insuranceBonus = contractor.insurance_details?.liability?.coverage ? +1 : 0;
      const contractorSafetyScore = Math.max(0, Math.min(10, baseSafetyScore - safetyRiskPenalty + insuranceBonus));
      
      // Execution score with timeline clarity
      const timelineBonus = contractor.timeline_details?.estimated_total_project_duration ? +0.5 : -0.5;
      const executionScore = calculateCategoryScore(riskCategories.execution, {
        timeline_clarity: timelineBonus,
        permits: contractor.permits?.Contractor?.types ? +0.5 : -0.5
      });
      
      // Legal score with licensing consideration
      const licenseBonus = contractor.license_details?.license_number ? +1 : -1;
      const legalScore = calculateCategoryScore(riskCategories.legal, {
        licensing: licenseBonus,
        warranty: contractor.warranty_details?.workmanship?.warranty_duration ? +0.5 : -0.5
      });
      
      // Communication score with documentation quality
      const docScore = safeParseScore(contractorEval?.documentation_and_deliverables?.score);
      const docBonus = docScore > 7 ? +1 : 0;
      const communicationScore = calculateCategoryScore(riskCategories.communication, {
        documentation_quality: docBonus
      });
      
      // Missing information assessment with impact
      const criticalMissing = missing.filter(item => 
        ['safety_measurements', 'insurance', 'license', 'warranty'].includes(item)
      ).length;
      const missingInfoImpact = missing.length * 0.3; // Reduced penalty
      
      // Calculate data confidence based on available information
      const calculateConfidence = () => {
        const requiredFields = [
          contractor.license_details?.license_number,
          contractor.insurance_details?.liability?.coverage,
          contractor.payment_terms?.total_cost,
          contractor.timeline_details?.estimated_total_project_duration,
          contractorEval?.safety_measurements?.score
        ];
        
        const availableData = requiredFields.filter(field => field).length;
        const confidencePercentage = (availableData / requiredFields.length) * 100;
        
        if (confidencePercentage >= 80) return { level: 'High', percentage: confidencePercentage, color: '#28a745' };
        if (confidencePercentage >= 60) return { level: 'Medium', percentage: confidencePercentage, color: '#ffc107' };
        return { level: 'Low', percentage: confidencePercentage, color: '#dc3545' };
      };

      const confidence = calculateConfidence();
      
      // Payment structure risk assessment
      const paymentRisk = (() => {
        const depositAmount = parseFloat(contractor.payment_terms?.deposit_required?.replace(/[\$,]/g, '')) || 0;
        const totalCost = parseFloat(contractor.payment_terms?.total_cost?.replace(/[\$,]/g, '')) || 1;
        const depositPercentage = (depositAmount / totalCost) * 100;
        
        if (depositPercentage > 50) return 'High';
        if (depositPercentage > 25) return 'Medium';
        return 'Low';
      })();

      // Insurance coverage assessment
      const insuranceCoverage = contractor.insurance_details?.liability?.coverage;
      const insuranceRisk = (() => {
        if (!insuranceCoverage) return 'High';
        const coverage = parseFloat(insuranceCoverage.replace(/[\$,]/g, '')) || 0;
        if (coverage >= 2000000) return 'Low';
        if (coverage >= 1000000) return 'Medium';
        return 'High';
      })();

      // Calculate overall performance score (weighted average)
      const rawOverallScore = (
        financialScore * 0.25 +
        contractorSafetyScore * 0.3 + // Higher weight for safety
        executionScore * 0.2 +
        legalScore * 0.15 +
        communicationScore * 0.1
      ) - missingInfoImpact;

      const overallPerformanceScore = Math.max(0, Math.min(10, rawOverallScore));

      // Convert scores to ranges with insights
      const getPerformanceRange = (score, categoryName, risks = []) => {
        let range, description, insights = [];
        
        if (score >= 8.5) {
          range = 'Excellent (8.5-10)';
          description = `Outstanding ${categoryName.toLowerCase()} performance`;
          insights.push(`Strong ${categoryName.toLowerCase()} management`);
        } else if (score >= 7) {
          range = 'Good (7-8.5)';
          description = `Solid ${categoryName.toLowerCase()} performance`;
          insights.push(`Generally reliable ${categoryName.toLowerCase()} practices`);
        } else if (score >= 5) {
          range = 'Fair (5-7)';
          description = `Adequate ${categoryName.toLowerCase()} with concerns`;
          insights.push(`Some ${categoryName.toLowerCase()} issues need attention`);
        } else {
          range = 'Needs Improvement (0-5)';
          description = `Significant ${categoryName.toLowerCase()} concerns`;
          insights.push(`Major ${categoryName.toLowerCase()} improvements required`);
        }

        // Add specific insights based on risks
        if (risks.length > 0) {
          const criticalRisks = risks.filter(r => r.severity === 'critical').length;
          const highRisks = risks.filter(r => r.severity === 'high').length;
          
          if (criticalRisks > 0) {
            insights.push(`${criticalRisks} critical issue${criticalRisks > 1 ? 's' : ''} identified`);
          }
          if (highRisks > 0) {
            insights.push(`${highRisks} high-priority concern${highRisks > 1 ? 's' : ''}`);
          }
        }

        return { range, description, insights, color: getScoreColor(score) };
      };

      // Helper function for color coding
      const getScoreColor = (score) => {
        if (score >= 7) return '#28a745';
        if (score >= 4) return '#ffc107';
        return '#dc3545';
      };

      // Performance ranges for each category
      const performanceRanges = {
        overall: getPerformanceRange(overallPerformanceScore, 'Overall'),
        financial: getPerformanceRange(financialScore, 'Financial', riskCategories.financial),
        safety: getPerformanceRange(contractorSafetyScore, 'Safety', riskCategories.safety),
        execution: getPerformanceRange(executionScore, 'Execution', riskCategories.execution),
        legal: getPerformanceRange(legalScore, 'Legal', riskCategories.legal),
        communication: getPerformanceRange(communicationScore, 'Communication', riskCategories.communication)
      };

      const performanceLevel = overallPerformanceScore >= 8 ? 'Excellent' : 
                              overallPerformanceScore >= 6 ? 'Good' : 
                              overallPerformanceScore >= 4 ? 'Fair' : 'Poor';
      
      return {
        name: contractor.contractor.contractor_name,
        shortName: contractor.contractor.contractor_name.split(' ')[0],
        overallPerformanceScore,
        performanceLevel,
        riskCategories,
        financialScore,
        safetyScore: contractorSafetyScore,
        executionScore,
        legalScore,
        communicationScore,
        criticalMissing,
        paymentRisk,
        insuranceRisk,
        totalRisks,
        missing,
        confidence,
        performanceRanges
      };
    });
  };

  return (
    <div className={styles.detailedAnalysisContainer}>
      <div className={styles.analysisHeader}>
        <h2>Comprehensive Detailed Analysis</h2>
        <p>Deep-dive analysis with professional insights to help you make the best decision</p>
      </div>

      {/* Section Navigation */}
      <div className={styles.sectionNav}>
        {sections.map(section => {
          const isAccessible = isSectionAccessible(section);
          return (
            <button
              key={section.id}
              className={`${styles.sectionButton} ${activeSection === section.id ? styles.active : ''} ${!isAccessible ? styles.locked : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              <i className={`fas ${section.icon}`}></i>
              <span>{section.label}</span>
              {!isAccessible && <i className={`fas fa-lock ${styles.lockIcon}`}></i>}
            </button>
          );
        })}
      </div>

      {/* Section Content */}
      <div className={styles.sectionContent}>
        {(() => {
          const currentSection = sections.find(s => s.id === activeSection);
          const isCurrentSectionAccessible = currentSection ? isSectionAccessible(currentSection) : true;
          
          if (!isCurrentSectionAccessible) {
            const sectionInfo = {
              'financial-deep-dive': {
                title: 'Financial Deep Dive Analysis',
                description: 'Comprehensive financial analysis including cost breakdowns, payment structures, allowances analysis, and financial risk assessment.',
                requiredPlan: 'Complete'
              },
              'contract-analysis': {
                title: 'Contractor Analysis',
                description: 'Detailed analysis of warranties, payment terms, legal protections, and contract clauses to protect your investment.',
                requiredPlan: 'Complete'
              },
              'technical-specs': {
                title: 'Technical Specifications Analysis',
                description: 'In-depth analysis of materials, methods, scope coverage, and technical approaches used by each contractor.',
                requiredPlan: 'Complete'
              }
            };
            
            const info = sectionInfo[activeSection] || {
              title: 'Premium Analysis',
              description: 'Advanced analysis features available with higher tier plans.',
              requiredPlan: 'Complete'
            };
            
            return <LockedContent 
              sectionTitle={info.title}
              sectionDescription={info.description}
              requiredPlan={info.requiredPlan}
            />;
          }
          
          return null;
        })()}
        
        {activeSection === 'comprehensive-scoring' && isSectionAccessible(sections.find(s => s.id === 'comprehensive-scoring')) && (
          <div className={styles.scoringSection}>
            <div className={styles.sectionHeader}>
              <h3>Comprehensive Contractor Scoring</h3>
              <p>Detailed analysis across 10 critical evaluation categories</p>
            </div>

            {/* Total Score Rankings - Hero Section */}
            <div className={styles.analysisCard}>
              <h4>🏆 Final Rankings & Total Scores</h4>
              <div className={styles.totalScoreSection}>
                <div className={styles.leaderboard}>
                  {contractors
                    .map((contractor, index) => {
                      const allScores = comprehensiveScoring.map(cat => cat[contractor.contractor.contractor_name] || 0);
                      const totalScore = allScores.reduce((sum, score) => sum + score, 0);
                      const avgScore = totalScore / allScores.length;
                      return {
                        contractor,
                        totalScore,
                        avgScore,
                        maxPossible: allScores.length * 10
                      };
                    })
                    .sort((a, b) => b.avgScore - a.avgScore)
                    .map((item, rank) => (
                      <div key={item.contractor.contractor.contractor_name} className={`${styles.leaderboardItem} ${rank === 0 ? styles.winner : ''} ${rank === 1 ? styles.second : ''} ${rank === 2 ? styles.third : ''} ${rank === 3 ? styles.fourth : ''} ${rank === 4 ? styles.fifth : ''}`}>
                        <div className={styles.rankBadge}>
                          {rank === 0 && <i className="fas fa-crown"></i>}
                          {rank === 1 && <i className="fas fa-medal"></i>}
                          {rank === 2 && <i className="fas fa-award"></i>}
                          {rank === 3 && <i className="fas fa-star"></i>}
                          {rank === 4 && <i className="fas fa-thumbs-up"></i>}
                          <span className={styles.rankNumber}>#{rank + 1}</span>
                        </div>
                        
                        <div className={styles.contractorSummary}>
                          <div 
                            className={styles.contractorAvatar}
                            style={{ backgroundColor: contractorColors[item.contractor.contractor.contractor_name] }}
                          >
                            {item.contractor.contractor.contractor_name.split(' ').map(word => word[0]).join('')}
                          </div>
                          <div className={styles.contractorDetails}>
                            <h5>{item.contractor.contractor.contractor_name}</h5>
                            <div className={styles.scoreDisplay}>
                              <div className={styles.primaryScore}>
                                <span className={styles.primaryScoreNumber}>{item.avgScore.toFixed(1)}</span>
                                <span className={styles.scoreLabel}>/10</span>
                              </div>
                              <div className={styles.totalPoints}>
                                {item.totalScore.toFixed(0)}/{item.maxPossible} total points
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className={styles.performanceBar}>
                          <div 
                            className={styles.performanceProgress}
                            style={{ 
                              width: `${(item.avgScore / 10) * 100}%`,
                              backgroundColor: contractorColors[item.contractor.contractor.contractor_name]
                            }}
                          ></div>
                          <span className={styles.percentageScore}>{((item.avgScore / 10) * 100).toFixed(0)}%</span>
                        </div>
                        
                        {rank === 0 && (
                          <div className={styles.winnerBadge}>
                            <i className="fas fa-trophy"></i>
                            <span>Top Performer</span>
                          </div>
                        )}
                        
                        {rank === 1 && (
                          <div className={styles.secondBadge}>
                            <i className="fas fa-award"></i>
                            <span>Runner-up</span>
                          </div>
                        )}
                        
                        {rank === 2 && (
                          <div className={styles.thirdBadge}>
                            <i className="fas fa-certificate"></i>
                            <span>Bronze</span>
                          </div>
                        )}
                        
                        {rank === 3 && (
                          <div className={styles.fourthBadge}>
                            <i className="fas fa-star"></i>
                            <span>Strong</span>
                          </div>
                        )}
                        
                        {rank === 4 && (
                          <div className={styles.fifthBadge}>
                            <i className="fas fa-check-circle"></i>
                            <span>Solid</span>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
                
                {/* Score Gap Analysis */}
                <div className={styles.gapAnalysis}>
                  <h5>Performance Gap Analysis</h5>
                  {(() => {
                    const scores = contractors.map(contractor => {
                      const allScores = comprehensiveScoring.map(cat => cat[contractor.contractor.contractor_name] || 0);
                      const avgScore = allScores.reduce((sum, score) => sum + score, 0) / allScores.length;
                      return {
                        name: contractor.contractor.contractor_name,
                        score: avgScore
                      };
                    }).sort((a, b) => b.score - a.score);
                    
                    const leader = scores[0];
                    const gaps = scores.slice(1).map(contractor => ({
                      name: contractor.name,
                      gap: leader.score - contractor.score,
                      percentage: ((leader.score - contractor.score) / leader.score * 100)
                    }));
                    
                    return (
                      <div className={styles.gapList}>
                        <div className={styles.leaderInfo}>
                          <i className="fas fa-medal"></i>
                          <strong>{leader.name}</strong> leads with <strong>{leader.score.toFixed(1)}/10</strong>
                        </div>
                        {gaps.map((gap, index) => (
                          <div key={gap.name} className={styles.gapItem}>
                            <span className={styles.gapContractor}>{gap.name}</span>
                            <div className={styles.gapDetails}>
                              <span className={styles.gapPoints}>-{gap.gap.toFixed(1)} points</span>
                              <span className={styles.gapPercentage}>({gap.percentage.toFixed(1)}% behind)</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Overall Score Comparison */}
            <div className={styles.analysisCard}>
              <h4>Multi-Category Performance Radar</h4>
              
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={500}>
                  <RadarChart outerRadius={200} data={comprehensiveScoring}>
                    <PolarGrid />
                    <PolarAngleAxis 
                      dataKey="shortName" 
                      tick={{ fill: '#333', fontSize: 13 }}
                      tickFormatter={(value) => value.length > 25 ? `${value.substring(0, 25)}...` : value}
                    />
                    <PolarRadiusAxis domain={[0, 10]} tickCount={6} />
                    {contractors.map((contractor, index) => (
                      <Radar
                        key={contractor.contractor.contractor_name}
                        name={contractor.contractor.contractor_name.length > 20 ? 
                          contractor.contractor.contractor_name.substring(0, 17) + '...' : 
                          contractor.contractor.contractor_name}
                        dataKey={contractor.contractor.contractor_name}
                        stroke={activeContractors && activeContractors[contractor.contractor.contractor_name] ? contractorColors[contractor.contractor.contractor_name] : '#CBD5E1'}
                        fill={activeContractors && activeContractors[contractor.contractor.contractor_name] ? contractorColors[contractor.contractor.contractor_name] : '#CBD5E1'}
                        fillOpacity={activeContractors && activeContractors[contractor.contractor.contractor_name] ? 0.5 : 0.2}
                        animationDuration={1500}
                        strokeWidth={activeContractors && activeContractors[contractor.contractor.contractor_name] ? 2 : 1}
                        strokeOpacity={activeContractors && activeContractors[contractor.contractor.contractor_name] ? 1 : 0.3}
                      />
                    ))}
                    <Tooltip 
                      formatter={(value) => [`${value.toFixed(1)}/10`, '']}
                      contentStyle={{ borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none' }}
                      labelFormatter={(label) => `Category: ${label}`}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      onClick={(data) => {
                        // Find the original contractor name by matching the shortened version
                        const originalContractor = contractors.find(contractor => {
                          const shortName = contractor.contractor.contractor_name.length > 20 ? 
                            contractor.contractor.contractor_name.substring(0, 17) + '...' : 
                            contractor.contractor.contractor_name;
                          return shortName === data.dataKey || contractor.contractor.contractor_name === data.dataKey;
                        });
                        
                        const contractorName = originalContractor ? originalContractor.contractor.contractor_name : data.dataKey;
                        toggleContractor(contractorName);
                      }}
                      formatter={(value, entry) => {
                        // Find the original contractor name by matching the shortened version
                        const originalContractor = contractors.find(contractor => {
                          const shortName = contractor.contractor.contractor_name.length > 20 ? 
                            contractor.contractor.contractor_name.substring(0, 17) + '...' : 
                            contractor.contractor.contractor_name;
                          return shortName === value || contractor.contractor.contractor_name === value;
                        });
                        
                        const contractorName = originalContractor ? originalContractor.contractor.contractor_name : value;
                        const isActive = activeContractors && activeContractors[contractorName];
                        
                        return (
                          <span style={{ 
                            color: isActive ? '#334155' : '#94A3B8',
                            textDecoration: isActive ? 'none' : 'line-through',
                            marginRight: '10px',
                            cursor: 'pointer'
                          }}>
                            {value}
                          </span>
                        );
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Category-by-Category Breakdown */}
            <div className={styles.analysisCard}>
              <h4>Category Performance Breakdown</h4>
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart data={comprehensiveScoring} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="shortName" 
                      angle={-45}
                      textAnchor="end"
                      height={120}
                      fontSize={11}
                    />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Legend 
                      onClick={(data) => {
                        // Find the original contractor name by matching the shortened version
                        const originalContractor = contractors.find(contractor => {
                          const shortName = contractor.contractor.contractor_name.length > 20 ? 
                            contractor.contractor.contractor_name.substring(0, 17) + '...' : 
                            contractor.contractor.contractor_name;
                          return shortName === data.dataKey || contractor.contractor.contractor_name === data.dataKey;
                        });
                        
                        const contractorName = originalContractor ? originalContractor.contractor.contractor_name : data.dataKey;
                        toggleContractor(contractorName);
                      }}
                      formatter={(value, entry) => {
                        // Find the original contractor name by matching the shortened version
                        const originalContractor = contractors.find(contractor => {
                          const shortName = contractor.contractor.contractor_name.length > 20 ? 
                            contractor.contractor.contractor_name.substring(0, 17) + '...' : 
                            contractor.contractor.contractor_name;
                          return shortName === value || contractor.contractor.contractor_name === value;
                        });
                        
                        const contractorName = originalContractor ? originalContractor.contractor.contractor_name : value;
                        const isActive = activeContractors && activeContractors[contractorName];
                        
                        return (
                          <span style={{ 
                            color: isActive ? '#334155' : '#94A3B8',
                            textDecoration: isActive ? 'none' : 'line-through',
                            marginRight: '10px',
                            cursor: 'pointer'
                          }}>
                            {value}
                          </span>
                        );
                      }}
                    />
                    {contractors.map((contractor, index) => (
                      <Bar
                        key={contractor.contractor.contractor_name}
                        dataKey={contractor.contractor.contractor_name}
                        fill={activeContractors && activeContractors[contractor.contractor.contractor_name] ? contractorColors[contractor.contractor.contractor_name] : '#CBD5E1'}
                        fillOpacity={activeContractors && activeContractors[contractor.contractor.contractor_name] ? 1 : 0.3}
                        name={contractor.contractor.contractor_name}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>



            {/* Performance Summary Dashboard */}
            <div className={styles.analysisCard}>
              <h4>Performance Summary Dashboard</h4>
              <div className={styles.performanceDashboard}>
                {contractors.map((contractor, index) => {
                  const evalKey = `contractor_${index + 1}`;
                  const contractorEval = evaluationData?.[evalKey];
                  
                  if (!contractorEval) return null;

                  // Calculate various metrics
                  const allScores = comprehensiveScoring.map(cat => cat[contractor.contractor.contractor_name] || 0);
                  const avgScore = allScores.reduce((sum, score) => sum + score, 0) / allScores.length;
                  const maxScore = Math.max(...allScores);
                  const minScore = Math.min(...allScores);
                  const strongCategories = allScores.filter(score => score >= 8).length;
                  const weakCategories = allScores.filter(score => score <= 4).length;
                  
                  return (
                    <div key={contractor.contractor.contractor_name} className={styles.performanceCard}>
                      <div className={styles.performanceHeader}>
                        <div 
                          className={styles.contractorAvatar}
                          style={{ backgroundColor: contractorColors[contractor.contractor.contractor_name] }}
                        >
                          {contractor.contractor.contractor_name.split(' ').map(word => word[0]).join('')}
                        </div>
                        <div className={styles.performanceInfo}>
                          <h5>{contractor.contractor.contractor_name}</h5>
                          <div className={styles.overallRating}>
                            <span className={styles.ratingValue}>{avgScore.toFixed(1)}</span>
                            <span className={styles.ratingLabel}>/10</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className={styles.performanceMetrics}>
                        <div className={styles.metric}>
                          <div className={styles.metricLabel}>Peak Performance</div>
                          <div className={styles.metricValue}>{maxScore}/10</div>
                        </div>
                        <div className={styles.metric}>
                          <div className={styles.metricLabel}>Weakest Area</div>
                          <div className={styles.metricValue}>{minScore}/10</div>
                        </div>
                        <div className={styles.metric}>
                          <div className={styles.metricLabel}>Strong Categories</div>
                          <div className={styles.metricValue}>{strongCategories}</div>
                        </div>
                        <div className={styles.metric}>
                          <div className={styles.metricLabel}>Needs Improvement</div>
                          <div className={styles.metricValue}>{weakCategories}</div>
                        </div>
                      </div>
                      
                      <div className={styles.performanceIndicators}>
                        <div className={styles.consistencyBar}>
                          <div className={styles.consistencyLabel}>Consistency Score</div>
                          <div className={styles.consistencyProgress}>
                            <div 
                              className={styles.consistencyFill}
                              style={{ 
                                width: `${Math.max(0, 100 - ((maxScore - minScore) / 10 * 100))}%`,
                                backgroundColor: contractorColors[contractor.contractor.contractor_name]
                              }}
                            ></div>
                          </div>
                          <span className={styles.consistencyValue}>
                            {Math.max(0, 100 - ((maxScore - minScore) / 10 * 100)).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Strengths vs Weaknesses Analysis */}
            <div className={styles.analysisCard}>
              <h4>Strengths vs Weaknesses Analysis</h4>
              <div className={styles.strengthsWeaknessesGrid}>
                {contractors.map((contractor, index) => {
                  const evalKey = `contractor_${index + 1}`;
                  const contractorEval = evaluationData?.[evalKey];
                  
                  if (!contractorEval) return null;

                  // Identify strengths (score >= 8) and weaknesses (score <= 4)
                  const strengths = comprehensiveScoring
                    .filter(cat => (cat[contractor.contractor.contractor_name] || 0) >= 8)
                    .map(cat => ({ name: cat.shortName, score: cat[contractor.contractor.contractor_name] }));
                    
                  const weaknesses = comprehensiveScoring
                    .filter(cat => (cat[contractor.contractor.contractor_name] || 0) <= 4)
                    .map(cat => ({ name: cat.shortName, score: cat[contractor.contractor.contractor_name] }));

                  return (
                    <div key={contractor.contractor.contractor_name} className={styles.swAnalysisCard}>
                      <div className={styles.swHeader}>
                        <div 
                          className={styles.contractorAvatar}
                          style={{ backgroundColor: contractorColors[contractor.contractor.contractor_name] }}
                        >
                          {contractor.contractor.contractor_name.split(' ').map(word => word[0]).join('')}
                        </div>
                        <h5>{contractor.contractor.contractor_name}</h5>
                      </div>
                      
                      <div className={styles.swContent}>
                        <div className={styles.strengthsSection}>
                          <h6><i className="fas fa-trophy"></i> Top Strengths ({strengths.length})</h6>
                          {strengths.length > 0 ? (
                            <div className={styles.strengthsList}>
                              {strengths.slice(0, 4).map((strength, idx) => (
                                <div key={idx} className={styles.strengthItem}>
                                  <span className={styles.strengthName}>{strength.name}</span>
                                  <span className={styles.strengthScore}>{strength.score}/10</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className={styles.noItems}>No standout strengths identified</p>
                          )}
                        </div>
                        
                        <div className={styles.weaknessesSection}>
                          <h6><i className="fas fa-exclamation-triangle"></i> Areas for Improvement ({weaknesses.length})</h6>
                          {weaknesses.length > 0 ? (
                            <div className={styles.weaknessesList}>
                              {weaknesses.slice(0, 4).map((weakness, idx) => (
                                <div key={idx} className={styles.weaknessItem}>
                                  <span className={styles.weaknessName}>{weakness.name}</span>
                                  <span className={styles.weaknessScore}>{weakness.score}/10</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className={styles.noItems}>No significant weaknesses identified</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Competitive Ranking Table */}
            <div className={styles.analysisCard}>
              <h4>Detailed Category Rankings</h4>
              <p className={styles.categoryIntro}>
                Click on any category to see detailed explanations for each contractor's score based on their proposal analysis.
              </p>
              
              <div className={styles.rankingTable}>
                <div 
                  className={styles.rankingTableHeader}
                  style={{
                    gridTemplateColumns: `200px repeat(${contractors.length}, minmax(90px, 1fr)) 180px 40px`
                  }}
                >
                  <div className={styles.categoryColumn}>Category</div>
                  {contractors.map(contractor => (
                    <div key={contractor.contractor.contractor_name} className={styles.contractorColumn}>
                      {contractor.contractor.contractor_name.split(' ')[0]}
                    </div>
                  ))}
                  <div className={styles.winnerColumn}>Category Leader</div>
                  <div className={styles.expandColumn}></div>
                </div>
                
                {comprehensiveScoring.map((category, categoryIndex) => {
                  // Find the winner for this category
                  const scores = contractors.map(contractor => ({
                    name: contractor.contractor.contractor_name,
                    score: category[contractor.contractor.contractor_name] || 0
                  }));
                  const winner = scores.reduce((prev, current) => (prev.score > current.score) ? prev : current);
                  const isExpanded = expandedScoreExplanations[categoryIndex];
                  
                  return (
                    <React.Fragment key={categoryIndex}>
                      <div 
                        className={`${styles.rankingTableRow} ${styles.clickableRow}`}
                        style={{
                          gridTemplateColumns: `200px repeat(${contractors.length}, minmax(90px, 1fr)) 180px 40px`
                        }}
                        onClick={() => toggleScoreExplanation(categoryIndex)}
                      >
                        <div className={styles.categoryColumn}>
                          <span 
                            className={styles.categoryLink}
                            onClick={(e) => {
                              e.stopPropagation();
                              scrollToCategoryDefinition(category.shortName);
                            }}
                            title={`Click to learn more about ${category.shortName} evaluation`}
                          >
                            {category.shortName}
                            <i className="fas fa-info-circle"></i>
                          </span>
                        </div>
                        {contractors.map(contractor => {
                          const score = category[contractor.contractor.contractor_name] || 0;
                          const isWinner = contractor.contractor.contractor_name === winner.name;
                          
                          return (
                            <div 
                              key={contractor.contractor.contractor_name} 
                              className={`${styles.contractorColumn} ${isWinner ? styles.winnerCell : ''}`}
                            >
                              <span className={styles.scoreValue}>{score}</span>
                            </div>
                          );
                        })}
                        <div className={styles.winnerColumn}>
                          <div className={styles.winnerInfo}>
                            <span className={styles.winnerName}>{winner.name.split(' ')[0]}</span>
                            <span className={styles.winnerScore}>({winner.score}/10)</span>
                          </div>
                        </div>
                        <div className={styles.expandColumn}>
                          <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                        </div>
                      </div>
                      
                      {/* Score Explanations */}
                      {isExpanded && (
                        <div className={styles.scoreExplanationsRow}>
                          <div className={styles.scoreExplanationsContent}>
                            <h5>📊 Score Explanations for {category.shortName}</h5>
                            <div className={styles.contractorExplanations}>
                              {contractors.map((contractor, contractorIndex) => {
                                const explanation = getEvaluationExplanation(category.shortName, contractorIndex);
                                const score = category[contractor.contractor.contractor_name] || 0;
                                
                                return (
                                  <div key={contractorIndex} className={styles.contractorExplanation}>
                                    <div className={styles.explanationHeader}>
                                      <div 
                                        className={styles.contractorAvatar}
                                        style={{ backgroundColor: contractorColors[contractor.contractor.contractor_name] }}
                                      >
                                        {contractor.contractor.contractor_name.split(' ').map(word => word[0]).join('')}
                                      </div>
                                      <div className={styles.explanationHeaderInfo}>
                                        <h6>{contractor.contractor.contractor_name}</h6>
                                        <div className={styles.scoreAndVerdict}>
                                          <span className={`${styles.badgeScoreNumber} ${score >= 7 ? styles.goodScore : score >= 4 ? styles.averageScore : styles.poorScore}`}>
                                            {score}/10
                                          </span>
                                          {explanation && (
                                            <span className={`${styles.verdictBadge} ${styles[explanation.verdict.toLowerCase()]}`}>
                                              {explanation.verdict}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className={styles.explanationRationale}>
                                      {explanation ? (
                                        <p>{explanation.rationale}</p>
                                      ) : (
                                        <p className={styles.noExplanation}>
                                          Score based on overall proposal evaluation. No detailed explanation available for this category.
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Performance Radar by Category Groups */}
            <div className={styles.analysisCard}>
              <h4>Performance by Category Groups</h4>
              <div className={styles.categoryGroupsContainer}>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart 
                    data={contractors.map(contractor => {
                      // Group categories into logical groups
                      const communicationCategories = ['communication', 'documentation', 'contracting'];
                      const technicalCategories = ['scope', 'safety', 'logistics'];
                      const qualityCategories = ['labor', 'innovation', 'timeline'];
                      
                      const communicationScore = comprehensiveScoring
                        .filter(cat => communicationCategories.some(comm => cat.shortName.toLowerCase().includes(comm.toLowerCase())))
                        .reduce((sum, cat) => sum + (cat[contractor.contractor.contractor_name] || 0), 0) / 
                        Math.max(1, comprehensiveScoring.filter(cat => communicationCategories.some(comm => cat.shortName.toLowerCase().includes(comm.toLowerCase()))).length);
                        
                      const technicalScore = comprehensiveScoring
                        .filter(cat => technicalCategories.some(tech => cat.shortName.toLowerCase().includes(tech.toLowerCase())))
                        .reduce((sum, cat) => sum + (cat[contractor.contractor.contractor_name] || 0), 0) / 
                        Math.max(1, comprehensiveScoring.filter(cat => technicalCategories.some(tech => cat.shortName.toLowerCase().includes(tech.toLowerCase()))).length);
                        
                      const qualityScore = comprehensiveScoring
                        .filter(cat => qualityCategories.some(qual => cat.shortName.toLowerCase().includes(qual.toLowerCase())))
                        .reduce((sum, cat) => sum + (cat[contractor.contractor.contractor_name] || 0), 0) / 
                        Math.max(1, comprehensiveScoring.filter(cat => qualityCategories.some(qual => cat.shortName.toLowerCase().includes(qual.toLowerCase()))).length);
                      
                      return {
                        name: contractor.contractor.contractor_name.split(' ')[0],
                        Communication: communicationScore.toFixed(1),
                        Technical: technicalScore.toFixed(1),
                        Quality: qualityScore.toFixed(1)
                      };
                    })}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Communication" fill="#8884d8" name="Communication & Process" />
                    <Bar dataKey="Technical" fill="#82ca9d" name="Technical Execution" />
                    <Bar dataKey="Quality" fill="#ffc658" name="Quality & Innovation" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Category Definitions */}
            <div className={styles.analysisCard} id="category-definitions">
              <h4>📚 Understanding the Evaluation Categories</h4>
              <div className={styles.categoryMethodologyIntro}>
                <p className={styles.categoryIntro}>
                  The Multi-Category Performance Radar above shows scores (0-10) based on our AI analysis of each contractor's proposal documents. 
                  Here's how we evaluate and score each category:
                </p>
                
                <div className={styles.aiAnalysisNote}>
                  <div className={styles.aiIcon}>
                    <i className="fas fa-robot"></i>
                  </div>
                  <div className={styles.aiNoteContent}>
                    <strong>AI-Powered Analysis:</strong> Our system analyzes the actual proposal documents you uploaded, examining content quality, 
                    professional standards, completeness, and clarity to generate objective scores for each evaluation category.
                  </div>
                </div>
              </div>
              
              <p className={styles.categorySubIntro}>
                Each contractor is scored across multiple critical areas. Here's what each category means and why it matters for your project:
              </p>
              
              <div className={styles.categoryDefinitionsGrid}>
                {comprehensiveScoring.map((category, index) => {
                  // Define explanations for each category
                  const getCategoryExplanation = (shortName, fullName) => {
                    const explanations = {
                      'Communication': {
                        icon: 'fa-comments',
                        description: 'How effectively the contractor communicates with clients, responds to questions, and keeps you informed throughout the project. Includes communication protocols, designated points of contact, project update frequency, site access conditions, and working hours.',
                        importance: 'Poor communication leads to misunderstandings, delays, and frustration. Clear communication protocols prevent conflicts and ensure everyone stays aligned on project expectations.',
                        goodScore: 'Regular updates, prompt responses, clear explanations, designated project manager, defined communication schedule, clear point of contact information',
                        badScore: 'Delayed responses, unclear communication, lack of project updates, no designated contact person, vague communication protocols'
                      },
                      'Documentation': {
                        icon: 'fa-file-alt',
                        description: 'Quality and completeness of project documentation, contracts, specifications, and deliverables provided. Evaluates contract clarity, technical specifications detail, permit documentation, project drawings, material specifications, and overall professionalism of proposal presentation.',
                        importance: 'Detailed documentation protects both parties and ensures clear expectations. Professional documentation indicates attention to detail and reduces the risk of misunderstandings or disputes.',
                        goodScore: 'Comprehensive contracts, detailed specifications, clear timelines, professional presentation, permit documentation included, material specifications provided',
                        badScore: 'Vague proposals, missing details, incomplete documentation, poor presentation, unclear language, missing technical specifications'
                      },
                      'Logistics': {
                        icon: 'fa-truck',
                        description: 'Planning and execution capabilities including scheduling, resource management, project coordination, material procurement strategies, site logistics, and contingency planning. Evaluates how well the contractor plans for project execution and manages potential challenges.',
                        importance: 'Good logistics prevent delays, cost overruns, and project complications. Proper planning demonstrates professionalism and reduces the risk of project disruptions.',
                        goodScore: 'Detailed project timeline, clear milestones, resource planning, material lead times specified, contingency plans, site logistics addressed',
                        badScore: 'Vague scheduling, poor planning, lack of coordination details, no contingency planning, unclear material procurement strategy'
                      },
                      'Scope': {
                        icon: 'fa-list-ul',
                        description: 'Thoroughness and clarity of the work scope, including what is and isn\'t included in the project. Evaluates completeness of work breakdown, construction methods, material specifications, project sequencing, and protection measures for existing structures.',
                        importance: 'Clear scope prevents change orders and unexpected costs during construction. Detailed scope definitions protect both parties and ensure project expectations are met.',
                        goodScore: 'Detailed work breakdown, clear inclusions/exclusions, specific construction methods, material specifications, project sequencing outlined',
                        badScore: 'Vague scope, missing details, unclear boundaries, no construction methodology, unclear material specifications'
                      },
                      'Safety': {
                        icon: 'fa-hard-hat',
                        description: 'Safety protocols, insurance coverage, licensing, and risk management practices demonstrated. Includes liability insurance, workers\' compensation, licensing verification, OSHA compliance, warranty terms, and safety procedures for protecting property and occupants.',
                        importance: 'Proper safety measures protect your property and reduce liability risks. Comprehensive insurance and safety protocols demonstrate professionalism and financial responsibility.',
                        goodScore: 'Current licenses, comprehensive insurance, safety protocols, OSHA compliance mentioned, clear warranty terms, liability coverage specified',
                        badScore: 'Missing insurance, no safety plan, licensing concerns, unclear warranty, inadequate liability coverage, no OSHA compliance details'
                      },
                      'Innovation': {
                        icon: 'fa-lightbulb',
                        description: 'Creative solutions, modern techniques, technology adoption, and value-engineering suggestions. Evaluates use of advanced materials, innovative construction methods, energy-efficient solutions, technology integration, and creative problem-solving approaches.',
                        importance: 'Innovation can improve project outcomes, efficiency, and long-term value. Modern techniques often result in better quality, faster completion, and enhanced durability.',
                        goodScore: 'Creative solutions, modern techniques, value-added suggestions, energy-efficient options, advanced materials, technology integration',
                        badScore: 'Generic approach, outdated methods, no value engineering, traditional materials only, no efficiency improvements suggested'
                      },
                      'Labor': {
                        icon: 'fa-users',
                        description: 'Quality of workforce, subcontractor relationships, project team experience, and labor management. Evaluates worker qualifications, certifications, union/non-union status, subcontractor vetting, team experience levels, and workforce management practices.',
                        importance: 'Skilled labor directly impacts project quality, timeline, and final results. Experienced teams with proper certifications ensure better workmanship and fewer complications.',
                        goodScore: 'Experienced team, trusted subcontractors, skilled workforce, proper certifications, clear team structure, quality control measures',
                        badScore: 'Inexperienced workers, poor subcontractor management, unclear qualifications, no certifications mentioned, vague team structure'
                      },
                      'Timeline': {
                        icon: 'fa-calendar-alt',
                        description: 'Realistic scheduling, milestone planning, and demonstrated ability to complete projects on time. Evaluates project duration estimates, start/end dates, milestone definitions, weather contingencies, and schedule management practices.',
                        importance: 'Accurate timelines help you plan your life around the construction schedule. Realistic scheduling demonstrates professionalism and helps prevent project delays and cost overruns.',
                        goodScore: 'Realistic timeline, clear milestones, on-time track record, weather contingencies, detailed scheduling, buffer time included',
                        badScore: 'Unrealistic schedule, vague timeline, history of delays, no contingency planning, overly optimistic estimates, unclear milestones'
                      },
                      'Contracting': {
                        icon: 'fa-handshake',
                        description: 'Contract terms, payment structure, warranty offerings, and overall business practices. Evaluates payment schedules, contract validity periods, dispute resolution methods, change order policies, lien waiver clauses, and professional business conduct.',
                        importance: 'Fair contracting terms protect your investment and ensure professional service. Clear contract terms prevent disputes and establish mutual expectations for the business relationship.',
                        goodScore: 'Fair payment terms, comprehensive warranty, professional contracts, clear dispute resolution, reasonable change order policy, transparent terms',
                        badScore: 'Unfair terms, poor warranty, unprofessional practices, unclear contract language, excessive upfront payments, no dispute resolution process'
                      },
                      'Value': {
                        icon: 'fa-dollar-sign',
                        description: 'Overall value proposition considering cost, quality, service, and what\'s included in the price. Evaluates cost competitiveness, material quality specifications, service inclusions, allowances provided, and overall return on investment potential.',
                        importance: 'Best value doesn\'t always mean lowest price - it\'s about getting the most for your investment. Good value balances cost with quality, service, and comprehensive inclusions.',
                        goodScore: 'Competitive pricing, quality materials, comprehensive service, detailed allowances, good warranty coverage, transparent pricing',
                        badScore: 'Poor value, hidden costs, low-quality inclusions, minimal allowances, vague pricing, inadequate service coverage'
                      },
                      'Recordkeeping': {
                        icon: 'fa-folder',
                        description: 'Project documentation practices including photo documentation, as-built drawings, daily logs, inspection records, and maintenance manuals. Evaluates how the contractor plans to document work progress and provide records for future reference.',
                        importance: 'Proper recordkeeping provides proof of work completion, helps with future maintenance, and protects both parties in case of disputes or warranty claims.',
                        goodScore: 'Digital photo documentation, daily progress logs, as-built drawings provided, maintenance manuals, inspection records kept',
                        badScore: 'No documentation plan, minimal record keeping, no progress photos, missing as-built drawings, unclear maintenance information'
                      },
                      'Execution': {
                        icon: 'fa-cogs',
                        description: 'Overall project execution planning including material procurement strategies, site management, quality control processes, and coordination with other trades. Assesses the contractor\'s ability to manage complex project requirements.',
                        importance: 'Strong execution planning prevents delays, ensures quality outcomes, and demonstrates the contractor\'s experience managing similar projects successfully.',
                        goodScore: 'Detailed execution plan, quality control measures, material procurement strategy, trade coordination, site management protocols',
                        badScore: 'Vague execution plan, no quality control mentioned, unclear procurement strategy, poor coordination planning, minimal site management'
                      }
                    };
                    
                    // Find the best match for the category
                    const key = Object.keys(explanations).find(k => 
                      shortName.toLowerCase().includes(k.toLowerCase()) || 
                      fullName.toLowerCase().includes(k.toLowerCase())
                    );
                    
                    return explanations[key] || {
                      icon: 'fa-info-circle',
                      description: 'Evaluation of contractor performance in this specific area of expertise.',
                      importance: 'This category impacts overall project success and client satisfaction.',
                      goodScore: 'Strong performance and professional approach',
                      badScore: 'Areas for improvement or concerns identified'
                    };
                  };
                  
                  const explanation = getCategoryExplanation(category.shortName, category.category);
                  
                  return (
                    <div 
                      key={index} 
                      className={styles.categoryDefinitionCard}
                      id={`category-${category.shortName.toLowerCase()}`}
                    >
                      <div className={styles.categoryDefinitionHeader}>
                        <div className={styles.categoryIcon}>
                          <i className={`fas ${explanation.icon}`}></i>
                        </div>
                        <div className={styles.categoryTitleGroup}>
                          <h5>{category.shortName}</h5>
                          <span className={styles.categoryFullName}>{category.category}</span>
                        </div>
                      </div>
                      
                      <div className={styles.categoryDefinitionContent}>
                        <p className={styles.categoryDescription}>
                          {explanation.description}
                        </p>
                        
                        <div className={styles.categoryImportance}>
                          <strong>Why it matters:</strong> {explanation.importance}
                        </div>
                        
                        <div className={styles.categoryScoreExamples}>
                          <div className={styles.scoreExample}>
                            <div className={styles.scoreExampleHeader}>
                              <i className="fas fa-check-circle" style={{color: '#059669'}}></i>
                              <span>High Score (8-10)</span>
                            </div>
                            <p>{explanation.goodScore}</p>
                          </div>
                          
                          <div className={styles.scoreExample}>
                            <div className={styles.scoreExampleHeader}>
                              <i className="fas fa-exclamation-triangle" style={{color: '#dc2626'}}></i>
                              <span>Low Score (1-4)</span>
                            </div>
                            <p>{explanation.badScore}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeSection === 'financial-deep-dive' && isSectionAccessible(sections.find(s => s.id === 'financial-deep-dive')) && (
          <div className={styles.financialSection}>
            <div className={styles.sectionHeader}>
              <h3>Financial Deep Dive Analysis</h3>
              <p>Comprehensive financial analysis based on proposal data and payment terms</p>
            </div>

            {/* Financial Overview Dashboard */}
            <div className={styles.analysisCard}>
              <h4>💰 Financial Overview Dashboard</h4>
              <div className={styles.financialOverviewGrid}>
                {contractors.map((contractor, index) => {
                  const totalCost = parseFloat(contractor.payment_terms.total_cost.replace(/[\$,]/g, '')) || 0;
                  const allowancesTotal = Object.values(contractor.allowances?.direct_costs || {})
                    .reduce((sum, val) => {
                      const numVal = typeof val === 'string' ? parseFloat(val.replace(/[\$,]/g, '')) : val;
                      return sum + (numVal || 0);
                    }, 0);
                  const laborAndMaterials = totalCost - allowancesTotal;
                  const deposit = parseFloat(contractor.payment_terms.deposit_required?.replace(/[\$,]/g, '') || '0');
                  
                  return (
                    <div key={contractor.contractor.contractor_name} className={styles.financialOverviewCard}>
                      <div className={styles.financialCardHeader}>
                        <div 
                          className={styles.contractorAvatar}
                          style={{ backgroundColor: contractorColors[contractor.contractor.contractor_name] }}
                        >
                          {contractor.contractor.contractor_name.split(' ').map(word => word[0]).join('')}
                        </div>
                        <div className={styles.financialCardInfo}>
                          <h5>{contractor.contractor.contractor_name}</h5>
                          <div className={styles.totalCostBig}>${totalCost.toLocaleString()}</div>
                        </div>
                      </div>
                      
                      <div className={styles.financialMetrics}>
                        <div className={styles.financialMetric}>
                          <div className={styles.metricLabel}>Allowances</div>
                          <div className={styles.metricValue}>${allowancesTotal.toLocaleString()}</div>
                          <div className={styles.metricPercentage}>
                            {Math.round((allowancesTotal / totalCost) * 100)}% of total
                          </div>
                        </div>
                        
                        <div className={styles.financialMetric}>
                          <div className={styles.metricLabel}>Labor & Base</div>
                          <div className={styles.metricValue}>${laborAndMaterials.toLocaleString()}</div>
                          <div className={styles.metricPercentage}>
                            {Math.round((laborAndMaterials / totalCost) * 100)}% of total
                          </div>
                        </div>
                        
                        <div className={styles.financialMetric}>
                          <div className={styles.metricLabel}>Deposit Required</div>
                          <div className={styles.metricValue}>${deposit.toLocaleString()}</div>
                          <div className={styles.metricPercentage}>
                            {Math.round((deposit / totalCost) * 100)}% upfront
                          </div>
                        </div>
                        
                        <div className={styles.financialMetric}>
                          <div className={styles.metricLabel}>Payment Structure</div>
                          <div className={styles.metricValue}>{contractor.payment_terms.pricing_structure}</div>
                          <div className={styles.metricPercentage}>
                            {contractor.payment_terms.breakdown_notes ? 'Detailed plan' : 'Basic structure'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Cost Breakdown Analysis */}
            <div className={styles.analysisCard}>
              <h4>🏗️ Cost Structure Comparison</h4>
              <div className={styles.costStructureContainer}>
                <div className={styles.costBreakdownChart}>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={contractors.map(contractor => {
                      const totalCost = parseFloat(contractor.payment_terms.total_cost.replace(/[\$,]/g, '')) || 0;
                      const allowancesTotal = Object.values(contractor.allowances?.direct_costs || {})
                        .reduce((sum, val) => {
                          const numVal = typeof val === 'string' ? parseFloat(val.replace(/[\$,]/g, '')) : val;
                          return sum + (numVal || 0);
                        }, 0);
                      const laborAndMaterials = totalCost - allowancesTotal;
                      
                      return {
                        name: contractor.contractor.contractor_name.split(' ')[0],
                        'Labor & Materials': laborAndMaterials,
                        'Allowances': allowancesTotal,
                        'Total': totalCost
                      };
                    })}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value, name) => [`$${value.toLocaleString()}`, name]} />
                      <Legend />
                      <Bar dataKey="Labor & Materials" stackId="cost" fill="#0077cc" name="Labor & Base Materials" />
                      <Bar dataKey="Allowances" stackId="cost" fill="#ff9800" name="Client Choice Allowances" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className={styles.costInsightBox}>
                  <h5>💡 Cost Analysis Insights</h5>
                  {(() => {
                    if (contractors.length < 2) return <p>Add more contractors for comparison insights</p>;
                    
                    const costs = contractors.map(contractor => ({
                      name: contractor.contractor.contractor_name,
                      cost: parseFloat(contractor.payment_terms.total_cost.replace(/[\$,]/g, '')) || 0,
                      allowances: Object.values(contractor.allowances?.direct_costs || {})
                        .reduce((sum, val) => sum + (parseFloat(val.toString().replace(/[\$,]/g, '')) || 0), 0)
                    }));
                    
                    const sortedCosts = [...costs].sort((a, b) => b.cost - a.cost);
                    const highest = sortedCosts[0];
                    const lowest = sortedCosts[sortedCosts.length - 1];
                    const priceDiff = highest.cost - lowest.cost;
                    const allowanceDiff = highest.allowances - lowest.allowances;
                    
                    return (
                      <div className={styles.insightsList}>
                        <div className={styles.insightItem}>
                          <div className={styles.insightIcon}>💸</div>
                          <div className={styles.insightText}>
                            <strong>${priceDiff.toLocaleString()} price difference</strong> between highest and lowest bid
                          </div>
                        </div>
                        
                        <div className={styles.insightItem}>
                          <div className={styles.insightIcon}>🎨</div>
                          <div className={styles.insightText}>
                            Higher bid includes <strong>${allowanceDiff.toLocaleString()} more</strong> in allowances
                          </div>
                        </div>
                        
                        <div className={styles.insightItem}>
                          <div className={styles.insightIcon}>⚖️</div>
                          <div className={styles.insightText}>
                            Allowances represent {Math.round((highest.allowances / highest.cost) * 100)}% vs {Math.round((lowest.allowances / lowest.cost) * 100)}% of total cost
                          </div>
                        </div>
                        
                        <div className={styles.insightItem}>
                          <div className={styles.insightIcon}>🎯</div>
                          <div className={styles.insightText}>
                            Consider if higher allowances justify the <strong>{Math.round(((priceDiff / lowest.cost) * 100))}% price premium</strong>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Allowance Deep Dive Analysis */}
            <div className={styles.analysisCard}>
              <h4>🛠️ Allowance Breakdown Analysis</h4>
              <div className={styles.allowanceAnalysisContainer}>
                <div className={styles.allowanceComparisonChart}>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart 
                      data={(() => {
                        // Get all unique allowance categories across contractors
                        const allCategories = new Set();
                        contractors.forEach(contractor => {
                          Object.keys(contractor.allowances?.direct_costs || {}).forEach(key => {
                            allCategories.add(key);
                          });
                        });
                        
                        return Array.from(allCategories).map(category => {
                          const dataPoint = {
                            category: category.length > 15 ? category.substring(0, 15) + '...' : category
                          };
                          
                          contractors.forEach(contractor => {
                            const value = contractor.allowances?.direct_costs?.[category];
                            const numValue = typeof value === 'string' ? 
                              parseFloat(value.replace(/[\$,]/g, '')) || 0 : 
                              parseFloat(value) || 0;
                            dataPoint[contractor.contractor.contractor_name.split(' ')[0]] = numValue;
                          });
                          
                          return dataPoint;
                        });
                      })()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="category" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={11}
                      />
                      <YAxis tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '']} />
                      <Legend 
                        onClick={(data) => {
                          const originalContractor = contractors.find(contractor => 
                            contractor.contractor.contractor_name.includes(data.dataKey)
                          );
                          if (originalContractor) {
                            toggleContractor(originalContractor.contractor.contractor_name);
                          }
                        }}
                        formatter={(value, entry) => {
                          const originalContractor = contractors.find(contractor => 
                            contractor.contractor.contractor_name.includes(value)
                          );
                          const contractorName = originalContractor ? originalContractor.contractor.contractor_name : value;
                          const isActive = activeContractors && activeContractors[contractorName];
                          
                          return (
                            <span style={{ 
                              color: isActive ? '#334155' : '#94A3B8',
                              textDecoration: isActive ? 'none' : 'line-through',
                              marginRight: '10px',
                              cursor: 'pointer'
                            }}>
                              {value}
                            </span>
                          );
                        }}
                      />
                      {contractors.map((contractor, index) => (
                        <Bar 
                          key={contractor.contractor.contractor_name}
                          dataKey={contractor.contractor.contractor_name.split(' ')[0]}
                          fill={activeContractors && activeContractors[contractor.contractor.contractor_name] ? contractorColors[contractor.contractor.contractor_name] : '#CBD5E1'}
                          fillOpacity={activeContractors && activeContractors[contractor.contractor.contractor_name] ? 1 : 0.3}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className={styles.allowanceInsights}>
                  <h5>🔍 Allowance Analysis</h5>
                  {(() => {
                    const allowanceData = contractors.map(contractor => {
                      const allowances = contractor.allowances?.direct_costs || {};
                      const totalAllowances = Object.values(allowances)
                        .reduce((sum, val) => sum + (parseFloat(val.toString().replace(/[\$,]/g, '')) || 0), 0);
                      const totalCost = parseFloat(contractor.payment_terms.total_cost.replace(/[\$,]/g, '')) || 0;
                      
                      return {
                        name: contractor.contractor.contractor_name.split(' ')[0],
                        totalAllowances,
                        totalCost,
                        percentage: Math.round((totalAllowances / totalCost) * 100),
                        categories: Object.keys(allowances).length
                      };
                    });
                    
                    return (
                      <div className={styles.allowanceStrategies}>
                        {allowanceData.map(data => (
                          <div key={data.name} className={styles.strategyCard}>
                            <div className={styles.strategyHeader}>
                              <div className={styles.strategyIcon}>📊</div>
                              <h6>{data.name} Allowances</h6>
                            </div>
                            <div className={styles.strategyContent}>
                              <p><strong>${data.totalAllowances.toLocaleString()}</strong> total allowances</p>
                              <div className={styles.strategyPros}>
                                • {data.percentage}% of total project cost<br/>
                                • {data.categories} allowance categories<br/>
                                • {data.percentage > 25 ? 'High flexibility in choices' : 'Standard allowance coverage'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Payment Terms Analysis */}
            <div className={styles.analysisCard}>
              <h4>💳 Payment Structure Analysis</h4>
              <div className={styles.cashFlowContainer}>
                <div className={styles.paymentScheduleComparison}>
                  {contractors.map((contractor, index) => {
                    const totalCost = parseFloat(contractor.payment_terms.total_cost.replace(/[\$,]/g, '')) || 0;
                    const deposit = parseFloat(contractor.payment_terms.deposit_required?.replace(/[\$,]/g, '') || '0');
                    const hasDetailedSchedule = contractor.payment_terms.breakdown_notes && contractor.payment_terms.breakdown_notes.length > 50;
                    
                    return (
                      <div key={contractor.contractor.contractor_name} className={styles.paymentScheduleCard}>
                        <div className={styles.paymentCardHeader}>
                          <div 
                            className={styles.contractorAvatar}
                            style={{ backgroundColor: contractorColors[contractor.contractor.contractor_name] }}
                          >
                            {contractor.contractor.contractor_name.split(' ').map(word => word[0]).join('')}
                          </div>
                          <div className={styles.paymentCardInfo}>
                            <h5>{contractor.contractor.contractor_name.split(' ')[0]}</h5>
                            <div className={styles.paymentStructure}>{contractor.payment_terms.pricing_structure}</div>
                          </div>
                        </div>
                        
                        <div className={styles.cashFlowMetrics}>
                          <div className={styles.cashFlowMetric}>
                            <div className={styles.metricLabel}>Initial Deposit</div>
                            <div className={styles.metricValue}>${deposit.toLocaleString()}</div>
                            <div className={styles.metricNote}>
                              {Math.round((deposit / totalCost) * 100)}% of total cost
                            </div>
                          </div>
                          
                          <div className={styles.cashFlowMetric}>
                            <div className={styles.metricLabel}>Payment Plan</div>
                            <div className={styles.metricValue}>
                              {hasDetailedSchedule ? 'Structured' : 'Basic'}
                            </div>
                            <div className={styles.metricNote}>
                              {hasDetailedSchedule ? 'Milestone-based' : 'Simple structure'}
                            </div>
                          </div>
                          
                          <div className={styles.cashFlowMetric}>
                            <div className={styles.metricLabel}>Late Payment</div>
                            <div className={styles.metricValue}>
                              {contractor.payment_terms.penalties ? 'Penalties Apply' : 'Not Specified'}
                            </div>
                            <div className={styles.metricNote}>
                              {contractor.payment_terms.penalties || 'Check contract terms'}
                            </div>
                          </div>
                        </div>
                        
                        {contractor.payment_terms.breakdown_notes && (
                          <div className={styles.paymentDetails}>
                            <h6>Payment Schedule:</h6>
                            <p className={styles.paymentNotes}>
                              {contractor.payment_terms.breakdown_notes.length > 200 ? 
                                contractor.payment_terms.breakdown_notes.substring(0, 200) + '...' :
                                contractor.payment_terms.breakdown_notes
                              }
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Financial Risk Assessment */}
            <div className={styles.analysisCard}>
              <h4>⚠️ Financial Risk Assessment</h4>
              <div className={styles.riskAssessmentGrid}>
                {contractors.map((contractor, index) => {
                  const totalCost = parseFloat(contractor.payment_terms.total_cost.replace(/[\$,]/g, '')) || 0;
                  const deposit = parseFloat(contractor.payment_terms.deposit_required?.replace(/[\$,]/g, '') || '0');
                  const depositRatio = deposit / totalCost;
                  const hasWarranty = contractor.warranty_details?.workmanship?.warranty_duration;
                  const hasInsurance = contractor.insurance_details?.liability?.coverage;
                  const hasDetailedTerms = contractor.payment_terms.breakdown_notes && contractor.payment_terms.breakdown_notes.length > 50;
                  
                  // Calculate risk score based on actual data
                  let riskFactors = [];
                  if (depositRatio > 0.2) riskFactors.push('High upfront deposit required');
                  if (!hasWarranty) riskFactors.push('Limited warranty protection');
                  if (!hasInsurance) riskFactors.push('Insurance details not clear');
                  if (contractor.payment_terms.penalties) riskFactors.push('Late payment penalties');
                  if (!hasDetailedTerms) riskFactors.push('Payment terms lack detail');
                  
                  const riskLevel = riskFactors.length <= 1 ? 'Low' : riskFactors.length <= 3 ? 'Medium' : 'High';
                  const riskColor = riskLevel === 'Low' ? '#22c55e' : riskLevel === 'Medium' ? '#f59e0b' : '#ef4444';
                  
                  return (
                    <div key={contractor.contractor.contractor_name} className={styles.riskAssessmentCard}>
                      <div className={styles.riskCardHeader}>
                        <div 
                          className={styles.contractorAvatar}
                          style={{ backgroundColor: contractorColors[contractor.contractor.contractor_name] }}
                        >
                          {contractor.contractor.contractor_name.split(' ').map(word => word[0]).join('')}
                        </div>
                        <div className={styles.riskCardInfo}>
                          <h5>{contractor.contractor.contractor_name.split(' ')[0]}</h5>
                          <div className={styles.riskLevel} style={{ color: riskColor }}>
                            {riskLevel} Risk
                          </div>
                        </div>
                      </div>
                      
                      <div className={styles.riskFactors}>
                        <div className={styles.riskFactor}>
                          <div className={styles.riskFactorLabel}>Upfront Investment</div>
                          <div className={styles.riskFactorValue}>
                            ${deposit.toLocaleString()} ({Math.round(depositRatio * 100)}%)
                          </div>
                          <div className={styles.riskFactorStatus}>
                            {depositRatio > 0.2 ? '⚠️ High upfront risk' : '✅ Reasonable deposit'}
                          </div>
                        </div>
                        
                        <div className={styles.riskFactor}>
                          <div className={styles.riskFactorLabel}>Warranty Coverage</div>
                          <div className={styles.riskFactorValue}>
                            {hasWarranty || 'Not specified'}
                          </div>
                          <div className={styles.riskFactorStatus}>
                            {hasWarranty ? '✅ Protected' : '⚠️ Limited warranty'}
                          </div>
                        </div>
                        
                        <div className={styles.riskFactor}>
                          <div className={styles.riskFactorLabel}>Insurance Details</div>
                          <div className={styles.riskFactorValue}>
                            {hasInsurance || 'Not provided'}
                          </div>
                          <div className={styles.riskFactorStatus}>
                            {hasInsurance ? '✅ Covered' : '⚠️ Verify coverage'}
                          </div>
                        </div>
                        
                        {riskFactors.length > 0 && (
                          <div className={styles.riskFactor}>
                            <div className={styles.riskFactorLabel}>Risk Factors</div>
                            <div className={styles.riskFactorValue}>
                              {riskFactors.length} identified
                            </div>
                            <div className={styles.riskFactorStatus}>
                              {riskFactors.slice(0, 2).join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeSection === 'risk-assessment' && isSectionAccessible(sections.find(s => s.id === 'risk-assessment')) && (
          <div className={styles.riskSection}>
            <div className={styles.sectionHeader}>
              <h3>🛡️ Comprehensive Risk Assessment</h3>
              <p>AI-powered risk analysis with confidence scoring and actionable insights</p>
              <div className={styles.assessmentDisclaimer}>
                <i className="fas fa-info-circle"></i>
                <strong>Assessment Note:</strong> This is a preliminary risk assessment based on available data. 
                Professional due diligence is recommended before making final decisions.
              </div>
            </div>

            {/* Risk Overview Dashboard */}
            <div className={styles.analysisCard}>
              <h4>📊 Smart Risk Assessment Dashboard</h4>
              <div className={styles.riskAssessmentGrid}>
                {prepareAdvancedRiskAssessment().map(riskData => (
                  <div key={riskData.name} className={styles.riskAssessmentCard}>
                    <div className={styles.riskCardHeader}>
                      <div 
                        className={styles.contractorAvatar}
                        style={{ backgroundColor: contractorColors[riskData.name] }}
                      >
                        {riskData.name.split(' ').map(word => word[0]).join('')}
                      </div>
                      <div className={styles.riskCardInfo}>
                        <h5>{riskData.name}</h5>
                        <div className={`${styles.performanceLevel} ${styles[riskData.performanceLevel.toLowerCase()]}`}>
                          {riskData.performanceRanges.overall.range}
                        </div>
                        <div className={styles.confidenceIndicator}>
                          <span 
                            className={styles.confidenceBadge}
                            style={{ backgroundColor: riskData.confidence.color }}
                          >
                            {riskData.confidence.level} Confidence ({riskData.confidence.percentage.toFixed(0)}%)
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className={styles.riskFactors}>
                      <div className={styles.riskFactor}>
                        <div className={styles.riskFactorLabel}>Financial</div>
                        <div className={styles.riskFactorRange}>{riskData.performanceRanges.financial.range}</div>
                        <div className={`${styles.riskFactorStatus} ${riskData.financialScore >= 7 ? styles.good : riskData.financialScore >= 4 ? styles.medium : styles.poor}`}></div>
                      </div>
                      
                      <div className={styles.riskFactor}>
                        <div className={styles.riskFactorLabel}>Safety</div>
                        <div className={styles.riskFactorRange}>{riskData.performanceRanges.safety.range}</div>
                        <div className={`${styles.riskFactorStatus} ${riskData.safetyScore >= 7 ? styles.good : riskData.safetyScore >= 4 ? styles.medium : styles.poor}`}></div>
                      </div>
                      
                      <div className={styles.riskFactor}>
                        <div className={styles.riskFactorLabel}>Execution</div>
                        <div className={styles.riskFactorRange}>{riskData.performanceRanges.execution.range}</div>
                        <div className={`${styles.riskFactorStatus} ${riskData.executionScore >= 7 ? styles.good : riskData.executionScore >= 4 ? styles.medium : styles.poor}`}></div>
                      </div>
                      
                      <div className={styles.riskFactor}>
                        <div className={styles.riskFactorLabel}>Legal</div>
                        <div className={styles.riskFactorRange}>{riskData.performanceRanges.legal.range}</div>
                        <div className={`${styles.riskFactorStatus} ${riskData.legalScore >= 7 ? styles.good : riskData.legalScore >= 4 ? styles.medium : styles.poor}`}></div>
                      </div>
                    </div>

                    <div className={styles.keyInsights}>
                      <h6><i className="fas fa-lightbulb"></i> Key Insights</h6>
                      <ul className={styles.insightsList}>
                        {riskData.performanceRanges.overall.insights.slice(0, 2).map((insight, idx) => (
                          <li key={idx}>{insight}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Category Analysis */}
            <div className={styles.analysisCard}>
              <h4>Risk Category Breakdown</h4>
              
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={500}>
                  <RadarChart outerRadius={200} data={[
                    { category: 'Financial', ...prepareAdvancedRiskAssessment().reduce((acc, riskData) => ({ ...acc, [riskData.shortName]: riskData.financialScore }), {}) },
                    { category: 'Safety', ...prepareAdvancedRiskAssessment().reduce((acc, riskData) => ({ ...acc, [riskData.shortName]: riskData.safetyScore }), {}) },
                    { category: 'Execution', ...prepareAdvancedRiskAssessment().reduce((acc, riskData) => ({ ...acc, [riskData.shortName]: riskData.executionScore }), {}) },
                    { category: 'Legal', ...prepareAdvancedRiskAssessment().reduce((acc, riskData) => ({ ...acc, [riskData.shortName]: riskData.legalScore }), {}) },
                    { category: 'Communication', ...prepareAdvancedRiskAssessment().reduce((acc, riskData) => ({ ...acc, [riskData.shortName]: riskData.communicationScore }), {}) }
                  ]}>
                    <PolarGrid />
                    <PolarAngleAxis 
                      dataKey="category" 
                      tick={{ fill: '#333', fontSize: 13 }}
                      tickFormatter={(value) => value.length > 25 ? `${value.substring(0, 25)}...` : value}
                    />
                    <PolarRadiusAxis domain={[0, 10]} tickCount={6} />
                    {contractors.map((contractor, index) => {
                      const shortName = contractor.contractor.contractor_name.split(' ')[0];
                      return (
                        <Radar
                          key={contractor.contractor.contractor_name}
                          name={contractor.contractor.contractor_name.length > 20 ? 
                            contractor.contractor.contractor_name.substring(0, 17) + '...' : 
                            contractor.contractor.contractor_name}
                          dataKey={shortName}
                          stroke={activeContractors && activeContractors[contractor.contractor.contractor_name] ? contractorColors[contractor.contractor.contractor_name] : '#CBD5E1'}
                          fill={activeContractors && activeContractors[contractor.contractor.contractor_name] ? contractorColors[contractor.contractor.contractor_name] : '#CBD5E1'}
                          fillOpacity={activeContractors && activeContractors[contractor.contractor.contractor_name] ? 0.5 : 0.2}
                          animationDuration={1500}
                          strokeWidth={activeContractors && activeContractors[contractor.contractor.contractor_name] ? 2 : 1}
                          strokeOpacity={activeContractors && activeContractors[contractor.contractor.contractor_name] ? 1 : 0.3}
                        />
                      );
                    })}
                    <Tooltip 
                      formatter={(value) => [`${value.toFixed(1)}/10`, '']}
                      contentStyle={{ borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none' }}
                      labelFormatter={(label) => `Performance: ${label}`}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      onClick={(data) => {
                        // Find the original contractor name by matching the shortened version
                        const originalContractor = contractors.find(contractor => {
                          const shortName = contractor.contractor.contractor_name.length > 20 ? 
                            contractor.contractor.contractor_name.substring(0, 17) + '...' : 
                            contractor.contractor.contractor_name;
                          return shortName === data.dataKey || contractor.contractor.contractor_name === data.dataKey;
                        });
                        
                        const contractorName = originalContractor ? originalContractor.contractor.contractor_name : data.dataKey;
                        toggleContractor(contractorName);
                      }}
                      formatter={(value, entry) => {
                        // Find the original contractor name by matching the shortened version
                        const originalContractor = contractors.find(contractor => {
                          const shortName = contractor.contractor.contractor_name.length > 20 ? 
                            contractor.contractor.contractor_name.substring(0, 17) + '...' : 
                            contractor.contractor.contractor_name;
                          return shortName === value || contractor.contractor.contractor_name === value;
                        });
                        
                        const contractorName = originalContractor ? originalContractor.contractor.contractor_name : value;
                        const isActive = activeContractors && activeContractors[contractorName];
                        
                        return (
                          <span style={{ 
                            color: isActive ? '#334155' : '#94A3B8',
                            textDecoration: isActive ? 'none' : 'line-through',
                            marginRight: '10px',
                            cursor: 'pointer'
                          }}>
                            {value}
                          </span>
                        );
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Detailed Risk Analysis */}
            <div className={styles.analysisCard}>
              <h4>Detailed Risk Analysis & Mitigation Strategies</h4>
              <div className={styles.detailedRiskAnalysis}>
                {prepareAdvancedRiskAssessment().map(riskData => (
                  <div key={riskData.name} className={styles.contractorRiskDetail}>
                                         <div className={styles.contractorRiskHeader}>
                       <h5>{riskData.name}</h5>
                       <div className={`${styles.overallPerformanceBadge} ${styles[riskData.performanceLevel.toLowerCase()]}`}>
                         Overall Performance: {riskData.performanceLevel} ({riskData.overallPerformanceScore.toFixed(1)}/10)
                       </div>
                     </div>

                    <div className={styles.riskAnalysisGrid}>
                      {/* Financial Risk Analysis */}
                      <div className={styles.riskAnalysisSection}>
                        <h6><i className="fas fa-dollar-sign"></i> Financial Risk Analysis</h6>
                                                 <div className={styles.riskMetrics}>
                           <div className={styles.riskMetric}>
                             <span>Performance Level:</span>
                             <span className={`${styles.riskValue} ${riskData.financialScore >= 7 ? styles.good : riskData.financialScore >= 4 ? styles.medium : styles.poor}`}>
                               {riskData.financialScore >= 7 ? 'Good' : riskData.financialScore >= 4 ? 'Fair' : 'Poor'}
                             </span>
                           </div>
                          <div className={styles.riskMetric}>
                            <span>Payment Structure Risk:</span>
                            <span className={`${styles.riskValue} ${styles[riskData.paymentRisk.toLowerCase()]}`}>
                              {riskData.paymentRisk}
                            </span>
                          </div>
                        </div>
                        {riskData.riskCategories.financial.length > 0 && (
                          <div className={styles.riskItems}>
                            <strong>Financial Concerns:</strong>
                            <ul>
                              {riskData.riskCategories.financial.map((risk, idx) => (
                                <li key={idx} className={styles[risk.severity]}>
                                  <span className={styles.severityBadge}>{risk.description}</span>
                                  {risk.text}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className={styles.mitigationStrategy}>
                          <strong>Smart Recommendation:</strong>
                          <p>
                            {riskData.financialScore < 4 
                              ? "🚨 HIGH PRIORITY: Negotiate fixed-price contracts with detailed scope. Establish clear change order procedures and require detailed cost breakdowns for any additional work."
                              : riskData.financialScore < 7
                              ? "⚠️ Monitor budget closely and establish regular cost reviews. Consider contingency funds for potential overruns."
                              : "✅ Financial performance is strong. Standard contract monitoring should suffice."
                            }
                          </p>
                          <div className={styles.insightsList}>
                            {riskData.performanceRanges.financial.insights.map((insight, idx) => (
                              <div key={idx} className={styles.insight}>• {insight}</div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Safety Risk Analysis */}
                      <div className={styles.riskAnalysisSection}>
                        <h6><i className="fas fa-shield-alt"></i> Safety & Compliance Assessment</h6>
                        <div className={styles.riskMetrics}>
                          <div className={styles.riskMetric}>
                            <span>Safety Performance:</span>
                            <span className={`${styles.riskValue} ${riskData.safetyScore >= 7 ? styles.good : riskData.safetyScore >= 4 ? styles.medium : styles.poor}`}>
                              {riskData.performanceRanges.safety.range}
                            </span>
                          </div>
                          <div className={styles.riskMetric}>
                            <span>Insurance Coverage:</span>
                            <span className={`${styles.riskValue} ${styles[riskData.insuranceRisk.toLowerCase()]}`}>
                              {riskData.insuranceRisk} Risk
                            </span>
                          </div>
                        </div>
                        {riskData.riskCategories.safety.length > 0 && (
                          <div className={styles.riskItems}>
                            <strong>Safety Concerns:</strong>
                            <ul>
                              {riskData.riskCategories.safety.map((risk, idx) => (
                                <li key={idx} className={styles[risk.severity]}>
                                  <span className={styles.severityBadge}>{risk.description}</span>
                                  {risk.text}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className={styles.mitigationStrategy}>
                          <strong>Smart Recommendation:</strong>
                          <p>
                            {riskData.safetyScore < 4 
                              ? "🚨 CRITICAL: Require comprehensive safety plan and OSHA compliance documentation. Verify insurance certificates and consider additional coverage requirements."
                              : riskData.safetyScore < 7
                              ? "⚠️ Regular safety audits and compliance checks. Ensure all workers are properly trained and certified."
                              : "✅ Excellent safety practices demonstrated. Standard safety monitoring should suffice."
                            }
                          </p>
                          <div className={styles.insightsList}>
                            {riskData.performanceRanges.safety.insights.map((insight, idx) => (
                              <div key={idx} className={styles.insight}>• {insight}</div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Execution Risk Analysis */}
                      <div className={styles.riskAnalysisSection}>
                        <h6><i className="fas fa-cogs"></i> Project Execution Risk</h6>
                                                 <div className={styles.riskMetrics}>
                          <div className={styles.riskMetric}>
                            <span>Execution Performance:</span>
                            <span className={`${styles.riskValue} ${riskData.executionScore >= 7 ? styles.good : riskData.executionScore >= 4 ? styles.medium : styles.poor}`}>
                              {riskData.performanceRanges.execution.range}
                            </span>
                          </div>
                          <div className={styles.riskMetric}>
                            <span>Timeline Clarity:</span>
                            <span className={`${styles.riskValue} ${riskData.missing.includes('timeline') ? styles.high : styles.low}`}>
                              {riskData.missing.includes('timeline') ? 'Unclear' : 'Defined'}
                            </span>
                          </div>
                        </div>
                        {riskData.riskCategories.execution.length > 0 && (
                          <div className={styles.riskItems}>
                            <strong>Execution Concerns:</strong>
                            <ul>
                              {riskData.riskCategories.execution.map((risk, idx) => (
                                <li key={idx} className={styles[risk.severity]}>
                                  <span className={styles.severityBadge}>{risk.description}</span>
                                  {risk.text}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className={styles.mitigationStrategy}>
                          <strong>Smart Recommendation:</strong>
                          <p>
                            {riskData.executionScore < 4 
                              ? "🚨 HIGH PRIORITY: Establish detailed project milestones with penalties for delays. Require weekly progress reports and implement strict change order controls."
                              : riskData.executionScore < 7
                              ? "⚠️ Regular progress monitoring and milestone reviews. Maintain open communication channels for early issue identification."
                              : "✅ Strong execution capabilities demonstrated. Standard project management practices should be sufficient."
                            }
                          </p>
                          <div className={styles.insightsList}>
                            {riskData.performanceRanges.execution.insights.map((insight, idx) => (
                              <div key={idx} className={styles.insight}>• {insight}</div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Legal & Compliance Risk */}
                      <div className={styles.riskAnalysisSection}>
                        <h6><i className="fas fa-gavel"></i> Legal & Compliance Risk</h6>
                                                 <div className={styles.riskMetrics}>
                          <div className={styles.riskMetric}>
                            <span>Legal Performance:</span>
                            <span className={`${styles.riskValue} ${riskData.legalScore >= 7 ? styles.good : riskData.legalScore >= 4 ? styles.medium : styles.poor}`}>
                              {riskData.performanceRanges.legal.range}
                            </span>
                          </div>
                          <div className={styles.riskMetric}>
                            <span>Critical Missing Info:</span>
                            <span className={`${styles.riskValue} ${riskData.criticalMissing > 2 ? styles.high : riskData.criticalMissing > 0 ? styles.medium : styles.low}`}>
                              {riskData.criticalMissing} items
                            </span>
                          </div>
                        </div>
                        {riskData.riskCategories.legal.length > 0 && (
                          <div className={styles.riskItems}>
                            <strong>Legal Concerns:</strong>
                            <ul>
                              {riskData.riskCategories.legal.map((risk, idx) => (
                                <li key={idx} className={styles[risk.severity]}>
                                  <span className={styles.severityBadge}>{risk.description}</span>
                                  {risk.text}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className={styles.mitigationStrategy}>
                          <strong>Smart Recommendation:</strong>
                          <p>
                            {riskData.legalScore < 4 
                              ? "🚨 CRITICAL: Require complete documentation including licenses, insurance, and permits before contract signing. Consider legal review of contract terms."
                              : riskData.legalScore < 7
                              ? "⚠️ Verify all licensing and insurance requirements. Ensure contract includes appropriate protections and warranty terms."
                              : "✅ Strong legal compliance demonstrated. Standard contract verification should be sufficient."
                            }
                          </p>
                          <div className={styles.insightsList}>
                            {riskData.performanceRanges.legal.insights.map((insight, idx) => (
                              <div key={idx} className={styles.insight}>• {insight}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Risk Summary and Recommendations */}
                    <div className={styles.riskSummarySection}>
                      <h6><i className="fas fa-lightbulb"></i> Key Recommendations</h6>
                                             <div className={styles.recommendationsList}>
                         {riskData.overallPerformanceScore < 4 && (
                           <div className={styles.recommendationItem}>
                             <i className="fas fa-exclamation-triangle"></i>
                             <span><strong>Poor Performance Rating:</strong> Consider requesting additional guarantees, lower deposit requirements, and more frequent progress reviews.</span>
                           </div>
                         )}
                         {riskData.criticalMissing > 0 && (
                           <div className={styles.recommendationItem}>
                             <i className="fas fa-clipboard-check"></i>
                             <span><strong>Documentation Required:</strong> Request missing information ({riskData.missing.join(', ')}) before proceeding.</span>
                           </div>
                         )}
                         {riskData.paymentRisk === 'High' && (
                           <div className={styles.recommendationItem}>
                             <i className="fas fa-credit-card"></i>
                             <span><strong>Payment Structure:</strong> High upfront payment requirement. Consider negotiating a more balanced payment schedule.</span>
                           </div>
                         )}
                         {riskData.insuranceRisk === 'High' && (
                           <div className={styles.recommendationItem}>
                             <i className="fas fa-shield-alt"></i>
                             <span><strong>Insurance Coverage:</strong> Insufficient insurance coverage. Require increased liability limits or additional coverage.</span>
                           </div>
                         )}
                         {riskData.overallPerformanceScore >= 7 && (
                           <div className={styles.recommendationItem}>
                             <i className="fas fa-thumbs-up"></i>
                             <span><strong>Strong Performance:</strong> This contractor demonstrates excellent performance across all categories.</span>
                           </div>
                         )}
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Comparison Matrix */}
            <div className={styles.analysisCard}>
              <h4>Risk Comparison Matrix</h4>
              <div className={styles.riskComparisonTable}>
                                 <div className={styles.comparisonHeader}>
                   <div>Performance Category</div>
                   {prepareAdvancedRiskAssessment().map(riskData => (
                     <div key={riskData.name}>{riskData.shortName}</div>
                   ))}
                   <div>Best Performance</div>
                 </div>
                
                                 {['Financial', 'Safety', 'Execution', 'Legal', 'Communication'].map(category => {
                   const riskAssessments = prepareAdvancedRiskAssessment();
                   const categoryKey = `${category.toLowerCase()}Score`;
                   const highestScore = riskAssessments.reduce((prev, current) => 
                     (prev[categoryKey] > current[categoryKey]) ? prev : current
                   );
                  
                                     return (
                     <div key={category} className={styles.comparisonRow}>
                       <div className={styles.categoryName}>{category} Performance</div>
                       {riskAssessments.map(riskData => {
                         const score = riskData[categoryKey];
                         const isHighest = riskData.name === highestScore.name;
                         return (
                           <div 
                             key={riskData.name} 
                             className={`${styles.scoreCell} ${isHighest ? styles.winner : ''} ${score >= 7 ? styles.good : score >= 4 ? styles.medium : styles.poor}`}
                           >
                             {score.toFixed(1)}
                             {isHighest && <i className="fas fa-crown"></i>}
                           </div>
                         );
                       })}
                       <div className={styles.winnerCell}>
                         {highestScore.shortName} ({highestScore[categoryKey].toFixed(1)})
                       </div>
                     </div>
                   );
                })}
              </div>
            </div>

            {/* Risk Mitigation Action Plan */}
            <div className={styles.analysisCard}>
              <h4>Risk Mitigation Action Plan</h4>
              <div className={styles.actionPlanContainer}>
                <div className={styles.actionPlanGrid}>
                  <div className={styles.actionPlanCard}>
                    <div className={styles.actionPlanHeader}>
                      <i className="fas fa-calendar-check"></i>
                      <h6>Pre-Contract Actions</h6>
                    </div>
                    <ul className={styles.actionPlanList}>
                      <li>Verify all licensing and insurance documentation</li>
                      <li>Request references from recent similar projects</li>
                      <li>Clarify any missing information in proposals</li>
                      <li>Negotiate payment terms to reduce upfront costs</li>
                      <li>Establish clear change order procedures</li>
                    </ul>
                  </div>
                  
                  <div className={styles.actionPlanCard}>
                    <div className={styles.actionPlanHeader}>
                      <i className="fas fa-hard-hat"></i>
                      <h6>During Construction</h6>
                    </div>
                    <ul className={styles.actionPlanList}>
                      <li>Implement weekly progress reviews and site inspections</li>
                      <li>Monitor safety compliance and OSHA requirements</li>
                      <li>Maintain detailed photo documentation</li>
                      <li>Review all change orders before approval</li>
                      <li>Ensure timely permit acquisition and inspections</li>
                    </ul>
                  </div>
                  
                  <div className={styles.actionPlanCard}>
                    <div className={styles.actionPlanHeader}>
                      <i className="fas fa-check-circle"></i>
                      <h6>Project Completion</h6>
                    </div>
                    <ul className={styles.actionPlanList}>
                      <li>Conduct thorough final walkthrough</li>
                      <li>Obtain all warranty documentation</li>
                      <li>Verify code compliance and final inspections</li>
                      <li>Document any punch list items with completion dates</li>
                      <li>Secure lien waivers from all subcontractors</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'contract-analysis' && isSectionAccessible(sections.find(s => s.id === 'contract-analysis')) && (
          <div className={styles.contractSection}>
            <div className={styles.sectionHeader}>
              <h3>Contract Terms & Legal Analysis</h3>
              <p>Comprehensive analysis of warranties, payment terms, and legal protections</p>
            </div>

            {/* Warranty Comparison */}
            <div className={styles.analysisCard}>
              <h4>Warranty & Protection Analysis</h4>
              <div className={styles.warrantyGrid}>
                {contractors.map(contractor => {
                  const warranty = contractor.warranty_details?.workmanship;
                  const insurance = contractor.insurance_details?.liability;
                  
                  return (
                    <div key={contractor.contractor.contractor_name} className={styles.warrantyCard}>
                      <div className={styles.warrantyHeader}>
                        <div 
                          className={styles.contractorAvatar}
                          style={{ backgroundColor: contractorColors[contractor.contractor.contractor_name] }}
                        >
                          {contractor.contractor.contractor_name.split(' ').map(word => word[0]).join('')}
                        </div>
                        <h5>{contractor.contractor.contractor_name}</h5>
                      </div>
                      
                      <div className={styles.warrantyDetails}>
                        <div className={styles.warrantyItem}>
                          <div className={styles.warrantyLabel}>Workmanship Warranty</div>
                          <div className={styles.warrantyValue}>
                            {warranty?.warranty_duration || 'Not specified'}
                          </div>
                        </div>
                        
                        <div className={styles.warrantyItem}>
                          <div className={styles.warrantyLabel}>Liability Coverage</div>
                          <div className={styles.warrantyValue}>
                            {insurance?.coverage || 'Not specified'}
                          </div>
                        </div>
                        
                        <div className={styles.warrantyItem}>
                          <div className={styles.warrantyLabel}>License Status</div>
                          <div className={styles.warrantyValue}>
                            {contractor.license_details?.license_number ? 
                              `#${contractor.license_details.license_number}` : 'Not provided'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Payment Terms Comparison */}
            <div className={styles.analysisCard}>
              <h4>Payment Schedule Analysis</h4>
              <div className={styles.paymentTermsTable}>
                <div className={styles.tableHeader}>
                  <div>Contractor</div>
                  <div>Total Cost</div>
                  <div>Deposit</div>
                  <div>Payment Structure</div>
                  <div>Late Fees</div>
                </div>
                {contractors.map(contractor => {
                  const payment = contractor.payment_terms;
                  const totalCost = parseFloat(payment.total_cost.replace(/[\$,]/g, '')) || 0;
                  const deposit = parseFloat(payment.deposit_required?.replace(/[\$,]/g, '')) || 0;
                  
                  return (
                    <div key={contractor.contractor.contractor_name} className={styles.tableRow}>
                      <div className={styles.contractorName}>
                        {contractor.contractor.contractor_name.split(' ')[0]}
                      </div>
                      <div className={styles.costValue}>${totalCost.toLocaleString()}</div>
                      <div className={styles.depositValue}>
                        ${deposit.toLocaleString()} ({Math.round((deposit / totalCost) * 100)}%)
                      </div>
                      <div className={styles.structureValue}>{payment.pricing_structure}</div>
                      <div className={styles.feesValue}>
                        {payment.penalties || 'Not specified'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legal Protections Matrix */}
            <div className={styles.analysisCard}>
              <h4>Legal Protection Matrix</h4>
              <div className={styles.protectionMatrix}>
                {contractors.map(contractor => {
                  const protections = [
                    { 
                      label: 'Liability Insurance', 
                      value: contractor.insurance_details?.liability?.coverage ? '✓' : '✗',
                      status: contractor.insurance_details?.liability?.coverage ? 'protected' : 'risk'
                    },
                    { 
                      label: 'Licensed Contractor', 
                      value: contractor.license_details?.license_number ? '✓' : '✗',
                      status: contractor.license_details?.license_number ? 'protected' : 'risk'
                    },
                    { 
                      label: 'Workmanship Warranty', 
                      value: contractor.warranty_details?.workmanship?.warranty_duration ? '✓' : '✗',
                      status: contractor.warranty_details?.workmanship?.warranty_duration ? 'protected' : 'risk'
                    },
                    { 
                      label: 'Change Order Policy', 
                      value: contractor.change_order_policy?.explanation ? '✓' : '✗',
                      status: contractor.change_order_policy?.explanation ? 'protected' : 'risk'
                    }
                  ];
                  
                  return (
                    <div key={contractor.contractor.contractor_name} className={styles.protectionCard}>
                      <h6>{contractor.contractor.contractor_name}</h6>
                      <div className={styles.protectionList}>
                        {protections.map((protection, index) => (
                          <div key={index} className={styles.protectionItem}>
                            <span className={styles.protectionLabel}>{protection.label}</span>
                            <span className={`${styles.protectionStatus} ${styles[protection.status]}`}>
                              {protection.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* AI Recommendation Engine Content */}
            {recommendationData && (
              <div className={styles.analysisCard}>
                <h4>🧠 AI-Powered Recommendation</h4>
                <div className={styles.aiRecommendationCard}>
                  <div className={styles.recommendedContractor}>
                    <div className={styles.recommendationBadge}>
                      <i className="fas fa-trophy"></i>
                      <span>AI Recommended</span>
                    </div>
                    <h3>{recommendationData.recommended_contractor}</h3>
                    <p className={styles.reasoningSummary}>
                      {recommendationData.reasoning_summary}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Detailed Contractor Rankings */}
            <div className={styles.analysisCard}>
              <h4>📊 Detailed Contractor Analysis & Rankings</h4>
              <div className={styles.contractorRankings}>
                {recommendationData?.ranking?.map((contractor, index) => (
                  <div key={contractor.contractor_name} className={styles.rankingCard}>
                    <div className={styles.rankingHeader}>
                      <div className={styles.rankPosition}>#{index + 1}</div>
                      <div className={styles.contractorInfo}>
                        <h5>{contractor.contractor_name}</h5>
                        <div className={styles.alignmentScore}>
                          <span className={styles.scoreLabel}>Alignment Score:</span>
                          <span className={styles.scoreValue}>{contractor.alignment_score}%</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className={styles.analysisContent}>
                      <div className={styles.strengthsSection}>
                        <h6><i className="fas fa-thumbs-up"></i> Key Strengths</h6>
                        <ul className={styles.strengthsList}>
                          {contractor.key_strengths?.slice(0, 3).map((strength, strengthIndex) => (
                            <li key={strengthIndex}>{strength}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className={styles.risksSection}>
                        <h6><i className="fas fa-exclamation-triangle"></i> Risks & Concerns</h6>
                        <ul className={styles.risksList}>
                          {contractor.risks_or_concerns?.slice(0, 2).map((risk, riskIndex) => (
                            <li key={riskIndex}>{risk}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className={styles.reasoningSection}>
                        <h6><i className="fas fa-brain"></i> AI Reasoning</h6>
                        <p className={styles.reasoningText}>{contractor.short_reasoning}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Key Decision Factors Analysis */}
            <div className={styles.analysisCard}>
              <h4>🎯 Key Decision Factors</h4>
              <div className={styles.decisionFactors}>
                <div className={styles.factorCard}>
                  <div className={styles.factorIcon}>
                    <i className="fas fa-dollar-sign"></i>
                  </div>
                  <div className={styles.factorContent}>
                    <h6>Cost Analysis</h6>
                    <p>Price difference of ${(Math.max(...financialAnalysis.map(f => f.totalCost)) - Math.min(...financialAnalysis.map(f => f.totalCost))).toLocaleString()} between highest and lowest bids</p>
                  </div>
                </div>
                
                <div className={styles.factorCard}>
                  <div className={styles.factorIcon}>
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <div className={styles.factorContent}>
                    <h6>Risk Assessment</h6>
                    <p>Comprehensive safety and compliance evaluation across all contractors</p>
                  </div>
                </div>
                
                <div className={styles.factorCard}>
                  <div className={styles.factorIcon}>
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className={styles.factorContent}>
                    <h6>Timeline Reliability</h6>
                    <p>Project duration and scheduling reliability analysis</p>
                  </div>
                </div>
                
                <div className={styles.factorCard}>
                  <div className={styles.factorIcon}>
                    <i className="fas fa-award"></i>
                  </div>
                  <div className={styles.factorContent}>
                    <h6>Quality Assurance</h6>
                    <p>Workmanship standards and professional credentials evaluation</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Items */}
            <div className={styles.analysisCard}>
              <h4>📋 Recommended Next Steps</h4>
              <div className={styles.actionItems}>
                <div className={styles.actionItem}>
                  <div className={styles.actionNumber}>1</div>
                  <div className={styles.actionContent}>
                    <h6>Contact {recommendationData?.recommended_contractor}</h6>
                    <p>Schedule a detailed walkthrough and discuss timeline specifics</p>
                  </div>
                </div>
                
                <div className={styles.actionItem}>
                  <div className={styles.actionNumber}>2</div>
                  <div className={styles.actionContent}>
                    <h6>Verify Insurance & Licensing</h6>
                    <p>Request current certificates and verify coverage amounts</p>
                  </div>
                </div>
                
                <div className={styles.actionItem}>
                  <div className={styles.actionNumber}>3</div>
                  <div className={styles.actionContent}>
                    <h6>Get References</h6>
                    <p>Contact recent clients for feedback on quality and reliability</p>
                  </div>
                </div>
                
                <div className={styles.actionItem}>
                  <div className={styles.actionNumber}>4</div>
                  <div className={styles.actionContent}>
                    <h6>Finalize Contract Terms</h6>
                    <p>Review payment schedule, change order policy, and warranty details</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'technical-specs' && isSectionAccessible(sections.find(s => s.id === 'technical-specs')) && (
          <div className={styles.technicalSection}>
            <div className={styles.sectionHeader}>
              <h3>Technical Specifications Analysis</h3>
              <p>Deep dive into materials, methods, and technical approaches</p>
            </div>

            {/* Materials Comparison */}
            <div className={styles.analysisCard}>
              <h4>Materials & Specifications Comparison</h4>
              <div className={styles.materialsGrid}>
                {contractors.map(contractor => {
                  const specs = contractor.specifications?.material_specification || {};
                  
                  return (
                    <div key={contractor.contractor.contractor_name} className={styles.materialsCard}>
                      <div className={styles.materialsHeader}>
                        <div 
                          className={styles.contractorAvatar}
                          style={{ backgroundColor: contractorColors[contractor.contractor.contractor_name] }}
                        >
                          {contractor.contractor.contractor_name.split(' ').map(word => word[0]).join('')}
                        </div>
                        <h5>{contractor.contractor.contractor_name}</h5>
                      </div>
                      
                      <div className={styles.materialsDetails}>
                        {Object.entries(specs).map(([key, value]) => (
                          <div key={key} className={styles.materialItem}>
                            <div className={styles.materialLabel}>
                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                            <div className={styles.materialValue}>{value}</div>
                          </div>
                        ))}
                        {Object.keys(specs).length === 0 && (
                          <div className={styles.noSpecs}>No detailed specifications provided</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Scope Coverage Analysis */}
            <div className={styles.analysisCard}>
              <h4>Project Scope Coverage Analysis</h4>
              <div className={styles.scopeCoverageChart}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={contractors.map((contractor, index) => {
                    const evalKey = `contractor_${index + 1}`;
                    const contractorEval = evaluationData?.[evalKey];
                    const categories = contractor.categories ? Object.keys(contractor.categories).length : 0;
                    const scopeScore = safeParseScore(contractorEval?.scope_and_methodology?.score);
                    
                    return {
                      name: contractor.contractor.contractor_name.split(' ')[0],
                      categories,
                      scopeScore,
                      coverage: contractorEval?.sow_coverage === 'full' ? 100 : 
                                contractorEval?.sow_coverage === 'partial' ? 75 : 50
                    };
                  })}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="categories" fill="#8884d8" name="Work Categories" />
                    <Bar yAxisId="left" dataKey="scopeScore" fill="#82ca9d" name="Scope Score (0-10)" />
                    <Bar yAxisId="right" dataKey="coverage" fill="#ffc658" name="SOW Coverage %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Timeline Analysis */}
            <div className={styles.analysisCard}>
              <h4>Project Timeline & Logistics</h4>
              <div className={styles.timelineGrid}>
                {contractors.map(contractor => {
                  const timeline = contractor.timeline_details;
                  const logistics = contractor.site_logistics;
                  
                  return (
                    <div key={contractor.contractor.contractor_name} className={styles.timelineCard}>
                      <h5>{contractor.contractor.contractor_name}</h5>
                      <div className={styles.timelineDetails}>
                        <div className={styles.timelineItem}>
                          <span className={styles.timelineLabel}>Estimated Duration:</span>
                          <span className={styles.timelineValue}>
                            {timeline?.estimated_total_project_duration || 'Not specified'}
                          </span>
                        </div>
                        <div className={styles.timelineItem}>
                          <span className={styles.timelineLabel}>Working Hours:</span>
                          <span className={styles.timelineValue}>
                            {logistics?.working_hours || 'Not specified'}
                          </span>
                        </div>
                        <div className={styles.timelineItem}>
                          <span className={styles.timelineLabel}>Site Access:</span>
                          <span className={styles.timelineValue}>
                            {logistics?.site_access ? 'Defined' : 'Not specified'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default DetailedAnalysis; 